/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as d3 from 'd3';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Stars } from '@react-three/drei';
import * as THREE from 'three';
// ── CSS ────────────────────────────────────────────────────
const CSS = `
@keyframes hv-fadein { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
@keyframes hv-pulse { 0%,100% { opacity:1; } 50% { opacity:0.5; } }
@keyframes hv-spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
@keyframes hv-glow { 0%,100% { opacity:0.4; } 50% { opacity:0.9; } }
@keyframes hv-edge { from { stroke-dashoffset:30; } to { stroke-dashoffset:0; } }
@keyframes hv-live-dot { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:0.4; transform:scale(0.7); } }
@keyframes hv-agent-ring { 0%,100% { stroke-opacity:.9; stroke-width:3px; } 50% { stroke-opacity:.3; stroke-width:5px; } }
`;
// ── Count-up animation hook ────────────────────────────────
function useCountUp(target, duration = 1200) {
 const [current, setCurrent] = useState(0);
 const prevTarget = useRef(0);
 const startTime = useRef(null);
 const startValue = useRef(0);
 useEffect(() => {
 if (target == null || target === prevTarget.current) return;
 startValue.current = prevTarget.current;
 prevTarget.current = target;
 startTime.current = performance.now();
 const animate = (now) => {
 const elapsed = now - startTime.current;
 const progress = Math.min(elapsed / duration, 1);
 const ease = 1 - Math.pow(1 - progress, 3);
 setCurrent(Math.round(startValue.current + (target - startValue.current) * ease));
 if (progress < 1) requestAnimationFrame(animate);
 };
 requestAnimationFrame(animate);
 }, [target, duration]);
 return current;
}
// ── Agent-id fra sti (agenter\koordinator\log.md → 'koordinator') ─────────
function deriveAgentId(sti) {
 if (!sti) return null;
 const m = sti.replace(/\\/g, '/').match(/^agenter\/([^/]+)\//);
 return m ? m[1] : null;
}
// ── node_type → gruppe mapping (firma_viden_noder) ───────────
const NODE_TYPE_GRUPPE = {
 procedure: 'virksomhed',
 fakta: 'laering',
 regel: 'beslutninger',
 begreb: 'sager',
 entitet: 'agenter',
 statistik: 'virksomhed',
};
// ── Gruppe fra sti eller node_type ────────────────────────────
function deriveGroupe(node) {
 // firma_viden noder har 'type' (node_type) men ingen hjernen/-sti
 if (node.type && NODE_TYPE_GRUPPE[node.type]) return NODE_TYPE_GRUPPE[node.type];
 if (!node.sti) return node.gruppe || 'other';
 const sti = node.sti.replace(/\\/g, '/');
 if (/^virksomhed\/|\/virksomhed\//.test(sti)) return 'virksomhed';
 if (/^laering\/sager\/|\/laering\/sager\//.test(sti)) return 'sager';
 if (/^laering\/|\/laering\//.test(sti)) return 'laering';
 if (/^beslutninger\/|\/beslutninger\//.test(sti)) return 'beslutninger';
 if (/^agenter\/|\/agenter\//.test(sti)) return 'agenter';
 if (/^scripts\/|\/scripts\//.test(sti)) return 'scripts';
 return node.gruppe || 'other';
}
// ── Farver per gruppe ──────────────────────────────────────
const GRUPPE_FARVER = {
 virksomhed: '#3fd4c7', // turkis/grøn — Firmaviden
 laering: '#c084fc', // lilla/pink — Læring
 sager: '#38bdf8', // lyseblå — Sager
 beslutninger: '#fb923c', // orange — Beslutninger
 agenter: '#60a5fa', // blå — Agenter
 scripts: '#6b7280', // grå — Scripts
 phantom: '#f59e0b', // orange/gul — Referencer
 other: '#94a3b8', // lys grå — Andet
 klientsager: '#16a34a', // grøn — Klient-sager
 sagsdokumenter: '#4ade80', // lysegrøn — Sagsdokumenter
};
const GRUPPE_LABELS = {
 virksomhed: 'Firmaviden',
 laering: 'Læring',
 sager: 'Sager',
 beslutninger: 'Beslutninger',
 agenter: 'Agenter',
 scripts: 'Scripts',
 phantom: 'Referencer',
 other: 'Andet',
 klientsager: 'Klient-sager',
 sagsdokumenter: 'Sagsdokumenter',
};
const ZOOM_DOK_THRESHOLD = 4; // Zoom-niveau der udfoldes dokumentnoder
// ── API helper ─────────────────────────────────────────────
const apiFetch = async (sti) => {
 const r = await fetch('/api' + sti, {
 credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 });
 if (!r.ok) throw new Error(`HTTP ${r.status}`);
 return r.json();
};
const fmt_dato = d => new Date(d).toLocaleString('da-DK', { dateStyle: 'medium', timeStyle: 'short' });
const fmt_kort = d => new Date(d).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
// ── Stat-kort ─────────────────────────────────────────────
function StatKort({ label, value, farve, ikon, delay = 0 }) {
 return (
 <div style={{
 background: `linear-gradient(135deg, ${farve}10, rgba(4,8,20,0.8))`,
 border: `1px solid ${farve}30`,
 borderRadius: 12,
 padding: '18px 22px',
 display: 'flex', flexDirection: 'column', gap: 4,
 animation: `hv-fadein 0.4s ease ${delay}s both`,
 position: 'relative', overflow: 'hidden',
 }}> <div style={{
 position: 'absolute', top: -8, right: -8,
 fontSize: 52, opacity: 0.06, lineHeight: 1,
 userSelect: 'none',
 }}>{ikon}</div> <div style={{ fontSize: 11, color: farve + 'aa', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2 }}>
 {label}
 </div> <div style={{ fontSize: 36, fontWeight: 800, color: farve, lineHeight: 1.1, fontFamily: '"JetBrains Mono","Fira Code",monospace' }}>
 {value ?? '—'}
 </div> <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${farve}60, transparent)` }} /> </div>
 );
}
// ── Vækst-tidslinje (SVG bar chart) ───────────────────────
function VaekstTidslinje({ tidslinje }) {
 const ref = useRef(null);
 useEffect(() => {
 if (!tidslinje?.length || !ref.current) return;
 const el = ref.current;
 const W = el.clientWidth;
 const H = 120;
 const margin = { top: 10, right: 8, bottom: 30, left: 24 };
 const w = W - margin.left - margin.right;
 const h = H - margin.top - margin.bottom;
 d3.select(el).selectAll('*').remove();
 const svg = d3.select(el).attr('width', W).attr('height', H);
 const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);
 // Udfyld manglende dage med 0
 const datoer = tidslinje.map(d => new Date(d.dato));
 const min_dato = d3.min(datoer);
 const max_dato = d3.max(datoer);
 const dagData = {};
 tidslinje.forEach(d => { dagData[d.dato] = d.antal; });
 const alle_dage = [];
 if (min_dato && max_dato) {
 let cur = new Date(min_dato);
 while (cur <= max_dato) {
 const noegle = cur.toISOString().slice(0, 10);
 alle_dage.push({ dato: noegle, antal: dagData[noegle] || 0 });
 cur = new Date(cur.getTime() + 86400000);
 }
 }
 const data = alle_dage.length ? alle_dage : tidslinje;
 const x = d3.scaleBand()
 .domain(data.map(d => d.dato))
 .range([0, w])
 .padding(0.25);
 const y = d3.scaleLinear()
 .domain([0, d3.max(data, d => d.antal) || 1])
 .range([h, 0])
 .nice();
 // X-akse (kun vis hver N. label)
 const step = Math.ceil(data.length / 8);
 g.append('g')
 .attr('transform', `translate(0,${h})`)
 .call(d3.axisBottom(x)
 .tickValues(data.filter((_, i) => i % step === 0).map(d => d.dato))
 .tickFormat(d => {
 const [, m, dag] = d.split('-');
 return `${dag}/${m}`;
 })
 )
 .call(ax => ax.select('.domain').remove())
 .call(ax => ax.selectAll('text').attr('fill', '#4a5a72').attr('font-size', 9))
 .call(ax => ax.selectAll('.tick line').attr('stroke', 'rgba(255,255,255,0.05)'));
 // Y-akse (kun 2-3 linjer)
 g.append('g')
 .call(d3.axisLeft(y).ticks(3).tickFormat(d => Math.round(d)))
 .call(ax => ax.select('.domain').remove())
 .call(ax => ax.selectAll('text').attr('fill', '#4a5a72').attr('font-size', 9))
 .call(ax => ax.selectAll('.tick line').attr('stroke', 'rgba(255,255,255,0.05)').attr('x2', w));
 // Søjler
 g.selectAll('rect')
 .data(data)
 .enter().append('rect')
 .attr('x', d => x(d.dato))
 .attr('y', h)
 .attr('width', x.bandwidth())
 .attr('height', 0)
 .attr('rx', 3)
 .attr('fill', d => d.antal > 0 ? '#3fd4c7' : '#1a2a3a')
 .attr('fill-opacity', d => d.antal > 0 ? 0.8 : 0.3)
 .style('filter', d => d.antal > 0 ? 'drop-shadow(0 0 4px rgba(63,212,199,0.4))' : 'none')
 .transition().duration(600).delay((_, i) => i * 15)
 .attr('y', d => y(d.antal))
 .attr('height', d => h - y(d.antal));
 }, [tidslinje]);
 return <svg ref={ref} style={{ width: '100%', display: 'block' }} />;
}
// ── node_type labels til panel ────────────────────────────
const NODE_TYPE_LABELS = {
 procedure: 'Procedure',
 fakta: 'Fakta',
 regel: 'Regel',
 begreb: 'Begreb',
 entitet: 'Entitet',
 statistik: 'Statistik',
};
// ── Fil-panel (klik på node) ───────────────────────────────
function FilPanel({ node, graph, onFocusNode, onClose }) {
 const [filData, setFilData] = useState(null);
 const [loading, setLoading] = useState(false);
 const [fejl, setFejl] = useState(null);
 const [viserMere, setMere] = useState(false);
 const [hubNoder, setHubNoder] = useState([]);
 const [hubTotal, setHubTotal] = useState(0);
 const [hubOffset, setHubOffset] = useState(0);
 const [hubLader, setHubLader] = useState(false);
 // hjernen/-filer hentes fra DB; firma_viden-noder har beskrivelse inline
 const isFirmaNode = !!(node?.type && NODE_TYPE_LABELS[node.type]);
 const isHub = node?.type === 'dokument_hub';
 const hubNodeType = isHub ? (node?.id || '').replace('hub-dok-', '') : null;
 useEffect(() => {
 if (!node?.sti) return;
 setLoading(true); setFilData(null); setFejl(null); setMere(false);
 apiFetch('/os/hjerne/fil?sti=' + encodeURIComponent(node.sti.replace(/\\/g, '/')))
 .then(r => setFilData(r))
 .catch(e => setFejl(e.message))
 .finally(() => setLoading(false));
 }, [node?.sti]);
 useEffect(() => {
 if (!isHub || !hubNodeType) { setHubNoder([]); setHubTotal(0); return; }
 setHubLader(true); setHubNoder([]); setHubOffset(0);
 apiFetch(`/os/hjerne/dok-liste?node_type=${encodeURIComponent(hubNodeType)}&limit=30&offset=0`)
 .then(r => { setHubNoder(r.noder || []); setHubTotal(r.total || 0); })
 .catch(() => {})
 .finally(() => setHubLader(false));
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [node?.id]);
 const hentFlereHub = () => {
 if (!hubNodeType) return;
 const nextOff = hubOffset + 30;
 setHubLader(true);
 apiFetch(`/os/hjerne/dok-liste?node_type=${encodeURIComponent(hubNodeType)}&limit=30&offset=${nextOff}`)
 .then(r => { setHubNoder(prev => [...prev, ...(r.noder || [])]); setHubOffset(nextOff); })
 .catch(() => {})
 .finally(() => setHubLader(false));
 };
 const forbundne = useMemo(() => {
 if (!node || !graph?.edges || !graph?.nodes) return [];
 const seen = new Set();
 return (graph.edges || [])
 .filter(e => {
 const src = typeof e.source === 'object' ? e.source?.id : e.source;
 const tgt = typeof e.target === 'object' ? e.target?.id : e.target;
 return src === node.id || tgt === node.id;
 })
 .map(e => {
 const src = typeof e.source === 'object' ? e.source?.id : e.source;
 const tgt = typeof e.target === 'object' ? e.target?.id : e.target;
 return src === node.id ? tgt : src;
 })
 .filter(id => { if (seen.has(id)) return false; seen.add(id); return true; })
 .map(id => graph.nodes.find(n => n.id === id))
 .filter(Boolean);
 }, [node, graph]);
 const farve = GRUPPE_FARVER[node?.gruppe] || GRUPPE_FARVER.other;
 const preview = filData?.indhold?.slice(0, 300) || '';
 const full = filData?.indhold || '';
 return (
 <div style={{
 width: 300, flexShrink: 0,
 background: 'rgba(4,8,20,0.95)', backdropFilter: 'blur(12px)',
 border: `1px solid ${farve}30`,
 borderRadius: 12, display: 'flex', flexDirection: 'column',
 overflow: 'hidden', animation: 'hv-fadein 0.2s ease',
 }}>
 {/* Header */}
 <div style={{
 padding: '14px 16px 12px',
 borderBottom: `1px solid ${farve}20`,
 background: `linear-gradient(135deg, ${farve}08, transparent)`,
 }}> <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}> <div style={{ flex: 1, minWidth: 0 }}> <div style={{ fontSize: 13, fontWeight: 700, color: farve, wordBreak: 'break-word', lineHeight: 1.3 }}>
 {node?.label || node?.id}
 </div> <div style={{ marginTop: 5, display: 'inline-flex', alignItems: 'center', gap: 4,
 padding: '2px 7px', borderRadius: 10, background: `${farve}15`,
 fontSize: 9, color: farve, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>
 {GRUPPE_LABELS[node?.gruppe] || node?.gruppe || 'Andet'}
 </div> </div> <button onClick={onClose} style={{
 background: 'none', border: 'none', cursor: 'pointer',
 color: '#3a4a5a', fontSize: 16, lineHeight: 1, padding: '0 0 0 8px', flexShrink: 0,
 }}>×</button> </div>
 {node?.sti && (
 <div style={{ marginTop: 8, fontSize: 9, color: '#3a4a5a', fontFamily: 'monospace',
 wordBreak: 'break-all', lineHeight: 1.4 }}>
 {node.sti.replace(/\\/g, '/')}
 </div>
 )}
 {isFirmaNode && node?.kilde_sti && (
 <div style={{ marginTop: 8, fontSize: 9, color: '#3a4a5a', fontFamily: 'monospace',
 wordBreak: 'break-all', lineHeight: 1.4 }}>
 {node.kilde_sti}
 </div>
 )}
 {isFirmaNode && node?.relevans != null && (
 <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}> <div style={{ fontSize: 9, color: '#3a4a5a' }}>Relevans</div> <div style={{ flex: 1, height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}> <div style={{ width: `${Math.round(node.relevans * 100)}%`, height: '100%', background: farve, borderRadius: 2 }} /> </div> <div style={{ fontSize: 9, color: farve, fontWeight: 700 }}>{Math.round(node.relevans * 100)}%</div> </div>
 )}
 {isFirmaNode && node?.type && (
 <div style={{ marginTop: 4, fontSize: 9, color: '#3a4a5a' }}>
 Type: <span style={{ color: farve, fontWeight: 700 }}>{NODE_TYPE_LABELS[node.type] || node.type}</span> </div>
 )}
 {filData?.oprettet_dato && (
 <div style={{ marginTop: 4, fontSize: 9, color: '#3a4a5a' }}>
 Oprettet: {new Date(filData.oprettet_dato).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })}
 </div>
 )}
 </div>
 {/* Indhold */}
 <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>
 {loading && (
 <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#3a4a5a', fontSize: 11 }}> <div style={{ width: 12, height: 12, border: `1.5px solid ${farve}30`, borderTop: `1.5px solid ${farve}`, borderRadius: '50%', animation: 'hv-spin 0.8s linear infinite' }} />
 Henter filindhold…
 </div>
 )}
 {fejl && (
 <div style={{ fontSize: 10, color: '#ef4444', padding: '6px 8px', background: 'rgba(239,68,68,0.08)', borderRadius: 6 }}>
 {fejl === 'HTTP 404' ? 'Filindhold ikke i cache — kør hjerne-filer-sync.ps1' : fejl}
 </div>
 )}
 {/* dokument_hub: vis individuelle noder med pagination */}
 {isHub && (
 <div> <div style={{ fontSize: 9, color: '#3a4a5a', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>
 {hubNodeType} — {hubTotal} dokumenter
 </div>
 {hubLader && hubNoder.length === 0 && (
 <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#3a4a5a', fontSize: 10 }}> <div style={{ width: 10, height: 10, border: '1.5px solid #3fd4c730', borderTop: '1.5px solid #3fd4c7', borderRadius: '50%', animation: 'hv-spin 0.8s linear infinite' }} />
 Henter…
 </div>
 )}
 <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
 {hubNoder.map((n, i) => (
 <div key={n.titel + i} style={{
 padding: '5px 8px', borderRadius: 6,
 background: 'rgba(63,212,199,0.05)', border: '1px solid rgba(63,212,199,0.12)',
 cursor: 'default',
 }}> <div style={{ fontSize: 10, color: '#a0b0c0', lineHeight: 1.4, wordBreak: 'break-word' }}>{n.titel}</div>
 {n.kilde_sti && (
 <div style={{ fontSize: 8, color: '#3a4a5a', fontFamily: 'monospace', marginTop: 2, wordBreak: 'break-all' }}>{n.kilde_sti}</div>
 )}
 </div>
 ))}
 </div>
 {hubNoder.length < hubTotal && (
 <button onClick={hentFlereHub} disabled={hubLader} style={{
 marginTop: 8, width: '100%', padding: '5px 0',
 background: 'rgba(63,212,199,0.08)', border: '1px solid rgba(63,212,199,0.2)',
 borderRadius: 6, cursor: hubLader ? 'default' : 'pointer',
 color: '#3fd4c7', fontSize: 10, fontWeight: 600,
 }}>
 {hubLader ? 'Henter…' : `Vis flere (${hubTotal - hubNoder.length} tilbage)`}
 </button>
 )}
 </div>
 )}
 {/* firma_viden node: vis beskrivelse direkte */}
 {isFirmaNode && node?.beskrivelse && (
 <div> <div style={{ fontSize: 9, color: '#3a4a5a', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>
 Beskrivelse
 </div> <div style={{ fontSize: 11, color: '#a0b0c0', lineHeight: 1.6 }}>
 {node.beskrivelse}
 </div> </div>
 )}
 {isFirmaNode && !node?.beskrivelse && (
 <div style={{ fontSize: 10, color: '#3a4a5a', fontStyle: 'italic' }}>Ingen beskrivelse tilgængelig</div>
 )}
 {/* hjernen/-fil: vis markdown-indhold fra DB */}
 {!loading && !isFirmaNode && filData?.indhold && (
 <div> <div style={{ fontSize: 9, color: '#3a4a5a', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>
 Indhold
 </div> <pre style={{
 fontSize: 10, color: '#7a8a9a', lineHeight: 1.55,
 whiteSpace: 'pre-wrap', wordBreak: 'break-word',
 margin: 0, fontFamily: '"Inter",sans-serif',
 maxHeight: viserMere ? 'none' : 160, overflow: 'hidden',
 }}>
 {viserMere ? full : preview}
 </pre>
 {full.length > 300 && (
 <button onClick={() => setMere(v => !v)} style={{
 marginTop: 6, background: 'none', border: 'none', cursor: 'pointer',
 color: farve, fontSize: 10, fontWeight: 700, padding: 0,
 }}>
 {viserMere ? '▲ Vis mindre' : `▼ Vis mere (${full.length} tegn)`}
 </button>
 )}
 </div>
 )}
 {/* Forbundne noder */}
 {forbundne.length > 0 && (
 <div style={{ marginTop: 14 }}> <div style={{ fontSize: 9, color: '#3a4a5a', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>
 Forbundne ({forbundne.length})
 </div> <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
 {forbundne.map(n => {
 const f2 = GRUPPE_FARVER[n.gruppe] || GRUPPE_FARVER.other;
 return (
 <button key={n.id} onClick={() => onFocusNode(n.id)} style={{
 background: `${f2}10`, border: `1px solid ${f2}30`,
 borderRadius: 6, padding: '3px 8px', cursor: 'pointer',
 color: f2, fontSize: 9, fontWeight: 600,
 transition: 'all 0.15s',
 }}
 onMouseEnter={e => { e.target.style.background = `${f2}25`; }}
 onMouseLeave={e => { e.target.style.background = `${f2}10`; }}
 >
 {(n.label || n.id).slice(0, 22)}
 </button>
 );
 })}
 </div> </div>
 )}
 </div> </div>
 );
}
// ── Sag-panel (klik på klientsager-node) ──────────────────
function SagPanel({ node, onClose }) {
 const [dokumenter, setDok] = useState(null);
 const [soegTerm, setSoeg] = useState('');
 const [soeger, setSoeger] = useState(false);
 const [resultater, setRes] = useState(null);
 const [fejl, setFejl] = useState(null);
 const sagId = (node?.sag_id > 0) ? node.sag_id
 : parseInt((node?.id || '').replace('sag-', ''), 10) || null;
 const farve = GRUPPE_FARVER.klientsager;
 useEffect(() => {
 if (!sagId) return;
 apiFetch(`/os/hjerne/sag/${sagId}/dokumenter`)
 .then(setDok)
 .catch(() => {});
 }, [sagId]);
 const søg = useCallback(async (term) => {
 if (!term || term.length < 2) return;
 setSoeger(true); setRes(null); setFejl(null);
 try {
 const r = await apiFetch(`/os/hjerne/soeg?sag_id=${sagId}&q=${encodeURIComponent(term)}`);
 setRes(r);
 } catch (e) { setFejl(e.message); }
 finally { setSoeger(false); }
 }, [sagId]);
 const handleKey = e => { if (e.key === 'Enter') søg(soegTerm); };
 const unikke_filer = dokumenter ? dokumenter.filter(d => !d.fil_navn.includes('(del ')).length
 + dokumenter.filter(d => d.fil_navn.includes('(del 1 af')).length : null;
 return (
 <div style={{
 width: 300, flexShrink: 0,
 background: 'rgba(4,8,20,0.95)', backdropFilter: 'blur(12px)',
 border: `1px solid ${farve}30`,
 borderRadius: 12, display: 'flex', flexDirection: 'column',
 overflow: 'hidden', animation: 'hv-fadein 0.2s ease',
 }}>
 {/* Header */}
 <div style={{
 padding: '14px 16px 12px',
 borderBottom: `1px solid ${farve}20`,
 background: `linear-gradient(135deg, ${farve}08, transparent)`,
 }}> <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}> <div style={{ flex: 1, minWidth: 0 }}> <div style={{ fontSize: 13, fontWeight: 700, color: farve, wordBreak: 'break-word', lineHeight: 1.3 }}>
 {node?.label || node?.id}
 </div> <div style={{ marginTop: 5, display: 'inline-flex', alignItems: 'center', gap: 4,
 padding: '2px 7px', borderRadius: 10, background: `${farve}15`,
 fontSize: 9, color: farve, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>
 Klient-sager
 </div> </div> <button onClick={onClose} style={{
 background: 'none', border: 'none', cursor: 'pointer',
 color: '#3a4a5a', fontSize: 16, lineHeight: 1, padding: '0 0 0 8px', flexShrink: 0,
 }}>×</button> </div>
 {/* Dok-tæller */}
 {dokumenter !== null && (
 <div style={{ marginTop: 10, display: 'flex', gap: 8 }}> <div style={{ padding: '5px 10px', borderRadius: 8, background: `${farve}12`, border: `1px solid ${farve}25`, textAlign: 'center' }}> <div style={{ fontSize: 18, fontWeight: 800, color: farve, fontFamily: 'monospace' }}>{node?.dok_antal ?? dokumenter.length}</div> <div style={{ fontSize: 9, color: farve + '99', textTransform: 'uppercase', letterSpacing: 0.7 }}>Poster</div> </div>
 {unikke_filer !== null && (
 <div style={{ padding: '5px 10px', borderRadius: 8, background: `${farve}08`, border: `1px solid ${farve}18`, textAlign: 'center' }}> <div style={{ fontSize: 18, fontWeight: 800, color: farve + 'cc', fontFamily: 'monospace' }}>{unikke_filer}</div> <div style={{ fontSize: 9, color: farve + '77', textTransform: 'uppercase', letterSpacing: 0.7 }}>Filer</div> </div>
 )}
 <div style={{ padding: '5px 8px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', textAlign: 'center', flex: 1 }}> <div style={{ fontSize: 9, color: '#4a5a72', lineHeight: 1.5, marginTop: 2 }}>Zoom {'>'}{ZOOM_DOK_THRESHOLD}x<br/>for dok-visning</div> </div> </div>
 )}
 </div>
 {/* Søgning */}
 <div style={{ padding: '10px 14px', borderBottom: `1px solid ${farve}10` }}> <div style={{ fontSize: 9, color: '#3a4a5a', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>
 Søg i dokumenter
 </div> <div style={{ display: 'flex', gap: 6 }}> <input
 value={soegTerm}
 onChange={e => setSoeg(e.target.value)}
 onKeyDown={handleKey}
 placeholder="Søgeord…"
 style={{
 flex: 1, padding: '6px 10px', borderRadius: 7, border: `1px solid ${farve}25`,
 background: 'rgba(255,255,255,0.04)', color: '#c8d4e0', fontSize: 11,
 outline: 'none', fontFamily: '"Inter",sans-serif',
 }}
 /> <button onClick={() => søg(soegTerm)} disabled={soeger} style={{
 padding: '6px 10px', borderRadius: 7, border: `1px solid ${farve}40`,
 background: `${farve}15`, color: farve, fontSize: 10, fontWeight: 700,
 cursor: soeger ? 'default' : 'pointer', opacity: soeger ? 0.6 : 1,
 }}>
 {soeger ? '…' : '↵'}
 </button> </div> </div>
 {/* Resultater / fil-liste */}
 <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px' }}>
 {fejl && <div style={{ fontSize: 10, color: '#ef4444', marginBottom: 8 }}>{fejl}</div>}
 {resultater !== null && (
 <div> <div style={{ fontSize: 9, color: '#3a4a5a', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>
 {resultater.length} resultater for "{soegTerm}"
 </div>
 {resultater.length === 0 && (
 <div style={{ fontSize: 10, color: '#4a5a72', textAlign: 'center', padding: '12px 0' }}>Ingen fund</div>
 )}
 {resultater.map((r, i) => (
 <div key={i} style={{
 marginBottom: 8, padding: '8px 10px',
 background: `${farve}08`, border: `1px solid ${farve}18`,
 borderRadius: 7, fontSize: 10,
 }}> <div style={{ color: farve, fontWeight: 600, marginBottom: 3, wordBreak: 'break-word' }}>
 {r.fil_navn}
 </div> <div style={{ color: '#6a7a8a', lineHeight: 1.5, wordBreak: 'break-word' }}>
 …{r.snippet}…
 </div> </div>
 ))}
 </div>
 )}
 {resultater === null && dokumenter !== null && (
 <div> <div style={{ fontSize: 9, color: '#3a4a5a', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>
 Alle filer ({dokumenter.length})
 </div>
 {dokumenter.map((d, i) => (
 <div key={i} style={{
 padding: '5px 8px', marginBottom: 4,
 background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)',
 borderRadius: 6, fontSize: 10, color: '#7a8a9a',
 display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6,
 }}> <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
 {d.fil_navn}
 </div> <div style={{ fontSize: 9, color: '#3a4a5a', whiteSpace: 'nowrap' }}>
 {d.format || '—'}
 </div> </div>
 ))}
 </div>
 )}
 {dokumenter === null && (
 <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#3a4a5a', fontSize: 11 }}> <div style={{ width: 12, height: 12, border: `1.5px solid ${farve}30`, borderTop: `1.5px solid ${farve}`, borderRadius: '50%', animation: 'hv-spin 0.8s linear infinite' }} />
 Henter dokumenter…
 </div>
 )}
 </div> </div>
 );
}
// ── Søg i alle noder (venstre panel i Videns-graf) ────────
const SOEG_TYPE_OPTIONS = [
 { value: '', label: 'Alle', farve: '#3fd4c7' },
 { value: 'fakta', label: 'Fakta', farve: '#22d3ee' },
 { value: 'begreb', label: 'Begreb', farve: '#a78bfa' },
 { value: 'entitet', label: 'Entitet', farve: '#fbbf24' },
 { value: 'regel', label: 'Regel', farve: '#f87171' },
 { value: 'procedure', label: 'Procedure', farve: '#34d399' },
 { value: 'statistik', label: 'Statistik', farve: '#60a5fa' },
 { value: 'email_modtaget', label: 'Email ind', farve: '#fb923c' },
 { value: 'email_udsendt', label: 'Email ud', farve: '#f97316' },
 { value: 'klient-meta', label: 'Klient-meta', farve: '#16a34a' },
 { value: 'juridisk-automation', label: 'Juridisk', farve: '#06b6d4' },
 { value: 'hjerne_fil', label: 'Filer (.md)', farve: '#8b5cf6' },
];
const SOEG_TYPE_FARVER = Object.fromEntries(SOEG_TYPE_OPTIONS.map(o => [o.value || '_alle', o.farve]));
function GrafSoegPanel({ onNodeSelect, selectedNodeId }) {
 const [q, setQ] = useState('');
 const [debouncedQ, setDebQ] = useState('');
 const [type, setType] = useState('');
 const [noder, setNoder] = useState([]);
 const [total, setTotal] = useState(0);
 const [offset, setOffset] = useState(0);
 const [lader, setLader] = useState(false);
 useEffect(() => {
 const t = setTimeout(() => setDebQ(q), 400);
 return () => clearTimeout(t);
 }, [q]);
 const doFetch = useCallback(async (qVal, typeVal, off) => {
 setLader(true);
 try {
 const p = new URLSearchParams({ limit: 50, offset: off });
 if (qVal) p.set('q', qVal);
 if (typeVal) p.set('type', typeVal);
 const r = await apiFetch(`/os/hjerne/alle-noder?${p}`);
 if (off === 0) setNoder(r.noder || []);
 else setNoder(prev => [...prev, ...(r.noder || [])]);
 setTotal(r.total || 0);
 setOffset(off);
 } catch (_) {}
 finally { setLader(false); }
 }, []);
 useEffect(() => { doFetch(debouncedQ, type, 0); }, [debouncedQ, type, doFetch]);
 return (
 <div style={{
 width: 290, flexShrink: 0,
 background: 'rgba(3,7,18,0.97)', backdropFilter: 'blur(12px)',
 border: '1px solid rgba(255,255,255,0.07)',
 borderRadius: 12, display: 'flex', flexDirection: 'column',
 overflow: 'hidden',
 }}>
 {/* Header */}
 <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0 }}> <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginBottom: 7 }}> <div style={{ fontSize: 14, color: '#3fd4c7', fontWeight: 800, fontFamily: 'monospace', lineHeight: 1 }}>
 {total > 0 ? total.toLocaleString('da-DK') : '…'}
 </div> <div style={{ fontSize: 9, color: type ? SOEG_TYPE_OPTIONS.find(o => o.value === type)?.farve + 'cc' : '#4a5a72', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>
 {type ? SOEG_TYPE_OPTIONS.find(o => o.value === type)?.label : 'noder total'}
 </div>
 {type && (
 <button onClick={() => setType('')} style={{
 marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
 color: '#3a4a5a', fontSize: 10, padding: '0 2px', lineHeight: 1,
 }} title="Vis alle"> alle</button>
 )}
 </div> <input
 value={q}
 onChange={e => setQ(e.target.value)}
 placeholder="Søg titel eller indhold…"
 style={{
 width: '100%', padding: '7px 10px', borderRadius: 8,
 border: '1px solid rgba(63,212,199,0.2)',
 background: 'rgba(255,255,255,0.04)', color: '#c8d4e0',
 fontSize: 11, outline: 'none', boxSizing: 'border-box',
 fontFamily: '"Inter",sans-serif',
 }}
 /> <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 7 }}>
 {SOEG_TYPE_OPTIONS.map(opt => {
 const aktiv = type === opt.value;
 const f = opt.farve;
 return (
 <button key={opt.value} onClick={() => setType(opt.value)} style={{
 padding: '2px 7px', borderRadius: 5, cursor: 'pointer',
 fontSize: 9, fontWeight: 700, letterSpacing: 0.3,
 border: `1px solid ${aktiv ? f + '80' : 'rgba(255,255,255,0.07)'}`,
 background: aktiv ? `${f}20` : 'transparent',
 color: aktiv ? f : '#3a4a5a',
 transition: 'all 0.12s',
 outline: aktiv && opt.value === '' ? `1px solid ${f}40` : 'none',
 }}>{opt.label}</button>
 );
 })}
 </div> </div>
 {/* Liste */}
 <div style={{ flex: 1, overflowY: 'auto' }}>
 {lader && noder.length === 0 && (
 <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 14px', color: '#2a3a50', fontSize: 11 }}> <div style={{ width: 12, height: 12, border: '1.5px solid rgba(63,212,199,0.2)', borderTop: '1.5px solid #3fd4c7', borderRadius: '50%', animation: 'hv-spin 0.8s linear infinite' }} />
 Henter…
 </div>
 )}
 {!lader && noder.length === 0 && (
 <div style={{ padding: '16px 14px', color: '#2a3a50', fontSize: 11, textAlign: 'center' }}>
 Ingen resultater
 </div>
 )}
 {noder.map((n, i) => {
 const farve = SOEG_TYPE_FARVER[n.type] || '#3fd4c7';
 const isSelected = selectedNodeId === (n.id || (n.titel + n.type));
 return (
 <div
 key={n.id || i}
 onClick={() => onNodeSelect({ ...n, gruppe: NODE_TYPE_GRUPPE[n.type] || (n.type === 'hjerne_fil' ? 'laering' : 'other') })}
 style={{
 padding: '7px 12px',
 borderBottom: '1px solid rgba(255,255,255,0.03)',
 cursor: 'pointer',
 background: isSelected ? `${farve}0d` : 'transparent',
 borderLeft: isSelected ? `2px solid ${farve}` : '2px solid transparent',
 transition: 'background 0.12s',
 }}
 onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.025)'; }}
 onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
 > <div style={{ display: 'flex', alignItems: 'flex-start', gap: 5 }}> <span style={{
 fontSize: 7, fontWeight: 700, padding: '2px 4px', borderRadius: 3,
 background: `${farve}14`, color: farve, flexShrink: 0, marginTop: 2,
 textTransform: 'uppercase', letterSpacing: 0.4, lineHeight: 1.2,
 }}>
 {(n.type || '').slice(0, 7)}
 </span> <div style={{ flex: 1, minWidth: 0 }}> <div style={{ fontSize: 10.5, color: '#b0c0d0', lineHeight: 1.3, fontWeight: 500, wordBreak: 'break-word' }}>
 {n.titel}
 </div>
 {n.kilde_sti && (
 <div style={{ fontSize: 8.5, color: '#2a3a4a', marginTop: 2, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
 title={n.kilde_sti.replace(/\\/g, '/')}>
 {n.kilde_sti.replace(/\\/g, '/').split('/').slice(-2).join('/')}
 </div>
 )}
 </div> </div> </div>
 );
 })}
 {noder.length > 0 && noder.length < total && (
 <button onClick={() => doFetch(debouncedQ, type, offset + 50)} disabled={lader} style={{
 display: 'block', width: '100%', padding: '8px 14px',
 background: 'none', border: 'none',
 color: lader ? '#2a3a50' : '#3fd4c7',
 fontSize: 10, fontWeight: 600, cursor: lader ? 'default' : 'pointer',
 textAlign: 'center',
 }}>
 {lader ? '↓ Henter…' : `↓ Vis ${Math.min(50, total - noder.length)} mere (${(total - noder.length).toLocaleString('da-DK')} tilbage)`}
 </button>
 )}
 </div> </div>
 );
}
// ── Canvas-baseret graf — viser ALLE noder direkte fra DB ────
const CANVAS_TYPE_GRUPPE = {
 procedure: 'virksomhed', fakta: 'laering', regel: 'beslutninger',
 begreb: 'sager', entitet: 'agenter', statistik: 'virksomhed',
 'klient-meta': 'sager', 'copilot-chat': 'agenter', 'ai-strategi': 'agenter',
 'juridisk-automation': 'beslutninger', projekter: 'beslutninger',
 strategi: 'beslutninger', lov: 'beslutninger',
 hjemmeside_kode: 'scripts', email_modtaget: 'virksomhed', email_udsendt: 'virksomhed',
};
function canvasNodeGruppe(n) {
 if (n.type === 'hjerne_fil') {
 const s = (n.kilde_sti || '').replace(/\\/g, '/');
 if (/^agenter\//.test(s)) return 'agenter';
 if (/^laering\/sager\//.test(s)) return 'sager';
 if (/^laering\//.test(s)) return 'laering';
 if (/^beslutninger\//.test(s)) return 'beslutninger';
 if (/^scripts\//.test(s)) return 'scripts';
 return 'scripts';
 }
 return CANVAS_TYPE_GRUPPE[n.type] || 'virksomhed';
}
const CANVAS_GRUPPE_ORDER = ['virksomhed', 'sager', 'agenter', 'laering', 'beslutninger', 'scripts'];
function buildCanvasLayout(noder) {
 const GOLDEN = 2.39996323;
 const GALAXY_SCALE = 400;
 const NODE_SP = 18;
 // Group by source document for edge computation + fallback positioning
 const byDoc = new Map();
 noder.forEach(n => {
 const doc = n.kilde_sti || ('__solo_' + n.id);
 if (!byDoc.has(doc)) byDoc.set(doc, []);
 byDoc.get(doc).push(n);
 });
 // Pre-compute galaxy fallback centres (used only for nodes without server layout)
 const docs = [...byDoc.keys()];
 const docPos = new Map();
 docs.forEach((doc, di) => {
 const gr = GALAXY_SCALE * Math.sqrt(di + 1);
 const ga = di * GOLDEN;
 docPos.set(doc, { cx: Math.cos(ga) * gr, cy: Math.sin(ga) * gr });
 });
 const docNodeIdx = new Map();
 const posMap = new Map();
 const gruppeAkkum = {};
 let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
 noder.forEach(n => {
 let x, y;
 if (n.graf_x != null && n.graf_y != null) {
 // Server-side Barnes-Hut force layout — organic scatter, looks like Obsidian
 x = n.graf_x; y = n.graf_y;
 } else {
 // Fallback galaxy spiral for nodes not yet in os.graf_layout
 const doc = n.kilde_sti || ('__solo_' + n.id);
 const dp = docPos.get(doc) || { cx: 0, cy: 0 };
 const i = docNodeIdx.get(doc) || 0;
 docNodeIdx.set(doc, i + 1);
 const lr = NODE_SP * Math.sqrt(i);
 const la = i * GOLDEN;
 x = dp.cx + (i > 0 ? Math.cos(la) * lr : 0);
 y = dp.cy + (i > 0 ? Math.sin(la) * lr : 0);
 }
 const g = canvasNodeGruppe(n);
 posMap.set(n.id, { x, y, gruppe: g, titel: n.titel || '', type: n.type || '', kilde_sti: n.kilde_sti || '', relevans: n.relevans || 0 });
 if (!gruppeAkkum[g]) gruppeAkkum[g] = { sx: 0, sy: 0, cnt: 0 };
 gruppeAkkum[g].sx += x; gruppeAkkum[g].sy += y; gruppeAkkum[g].cnt++;
 if (x < minX) minX = x; if (x > maxX) maxX = x;
 if (y < minY) minY = y; if (y > maxY) maxY = y;
 });
 const centers = {};
 CANVAS_GRUPPE_ORDER.forEach(g => {
 if (!gruppeAkkum[g]) return;
 const { sx, sy, cnt } = gruppeAkkum[g];
 centers[g] = { x: sx / cnt, y: sy / cnt, count: cnt };
 });
 const rawEdges = [];
 byDoc.forEach(nodes => {
 for (let i = 1; i < nodes.length; i++) {
 const pA = posMap.get(nodes[i - 1].id), pB = posMap.get(nodes[i].id);
 if (pA && pB) rawEdges.push(pA.x, pA.y, pB.x, pB.y);
 }
 });
 const edgeCoords = new Float32Array(rawEdges);
 return { posMap, centers, bbox: { minX, maxX, minY, maxY }, edgeCoords };
}
function CanvasGraf({ selectedNodeId, aktiveNodeIds = [], onNodeClick, focusNodeId }) {
 const ctnrRef = useRef(null), cvRef = useRef(null);
 const tfRef = useRef({ x: 0, y: 0, k: 0.04 });
 const posRef = useRef(new Map()), centersRef = useRef({}), edgesRef = useRef(new Float32Array(0));
 const bboxRef = useRef(null), rafRef = useRef(null);
 const dragRef = useRef(null), selRef = useRef(null);
 const aktRef = useRef([]);
 const fnRef = useRef({});
 const [lader, setLader] = useState(true);
 const [fejl, setFejl] = useState(null);
 const [total, setTotal] = useState(0);
 const [valgt, setValgt] = useState(null);
 useEffect(() => { aktRef.current = aktiveNodeIds; }, [aktiveNodeIds]);
 const doDraw = () => {
 const cv = cvRef.current;
 if (!cv || posRef.current.size === 0) return;
 const ctx = cv.getContext('2d');
 const { x: tx, y: ty, k } = tfRef.current;
 const W = cv.width, H = cv.height;
 ctx.clearRect(0, 0, W, H);
 // Group halos — soft glow clouds that make clusters visible at any zoom
 Object.entries(centersRef.current).forEach(([g, c]) => {
 if (!c.count || c.count < 5) return;
 const sx = c.x * k + tx, sy = c.y * k + ty;
 const r = Math.sqrt(c.count * 520) * k;
 if (r < 4 || sx < -r || sx > W + r || sy < -r || sy > H + r) return;
 const color = GRUPPE_FARVER[g] || '#94a3b8';
 const grad = ctx.createRadialGradient(sx, sy, r * 0.05, sx, sy, r);
 const a1 = k < 0.08 ? '28' : k < 0.2 ? '18' : '0c';
 const a2 = k < 0.08 ? '10' : '06';
 grad.addColorStop(0, color + a1);
 grad.addColorStop(0.55, color + a2);
 grad.addColorStop(1, color + '00');
 ctx.fillStyle = grad;
 ctx.beginPath(); ctx.arc(sx, sy, r, 0, Math.PI * 2); ctx.fill();
 });
 // Fixed screen-space dot radius — grows slightly with zoom so detail emerges
 const DOT_R = Math.max(1.2, Math.min(4, k * 28));
 const pad = DOT_R + 2;
 // Draw edges first (underneath dots)
 const ec = edgesRef.current;
 if (ec.length > 0) {
 const edgeAlpha = Math.min(0.55, 0.12 + k * 3.5);
 const alphaHex = Math.round(edgeAlpha * 255).toString(16).padStart(2, '0');
 ctx.lineWidth = 0.7;
 ctx.beginPath();
 ctx.strokeStyle = '#94a3b8' + alphaHex;
 for (let i = 0; i < ec.length; i += 4) {
 const sx1 = ec[i] * k + tx, sy1 = ec[i+1] * k + ty;
 const sx2 = ec[i+2] * k + tx, sy2 = ec[i+3] * k + ty;
 if ((sx1 < -20 && sx2 < -20) || (sx1 > W+20 && sx2 > W+20) ||
 (sy1 < -20 && sy2 < -20) || (sy1 > H+20 && sy2 > H+20)) continue;
 ctx.moveTo(sx1, sy1); ctx.lineTo(sx2, sy2);
 }
 ctx.stroke();
 }
 // Project to screen space + viewport cull
 const batches = {};
 posRef.current.forEach((p, id) => {
 const sx = p.x * k + tx, sy = p.y * k + ty;
 if (sx < -pad || sx > W + pad || sy < -pad || sy > H + pad) return;
 (batches[p.gruppe] = batches[p.gruppe] || []).push({ id, sx, sy, relevans: p.relevans, titel: p.titel });
 });
 // Draw dots (all zoom levels)
 Object.entries(batches).forEach(([g, ns]) => {
 ctx.fillStyle = (GRUPPE_FARVER[g] || '#94a3b8') + 'b0';
 ctx.beginPath();
 ns.forEach(({ sx, sy, relevans }) => {
 const r = relevans > 5 ? DOT_R * 2.2 : DOT_R;
 ctx.moveTo(sx + r, sy); ctx.arc(sx, sy, r, 0, Math.PI * 2);
 });
 ctx.fill();
 });
 // Group label hints at overview zoom (small fixed-size text, not scaled blobs)
 if (k < 0.18) {
 ctx.textAlign = 'center';
 Object.entries(centersRef.current).forEach(([g, c]) => {
 const sx = c.x * k + tx, sy = c.y * k + ty;
 if (sx < -160 || sx > W + 160 || sy < -30 || sy > H + 30) return;
 const f = GRUPPE_FARVER[g] || '#94a3b8';
 ctx.fillStyle = f + 'dd';
 ctx.font = 'bold 13px Inter,sans-serif';
 ctx.fillText(GRUPPE_LABELS[g] || g, sx, sy - 12);
 ctx.font = '10px Inter,sans-serif';
 ctx.fillStyle = f + '80';
 ctx.fillText(c.count.toLocaleString('da-DK'), sx, sy + 1);
 });
 }
 // Node labels — only at very high zoom so individual nodes are readable
 if (k > 1.8) {
 ctx.textAlign = 'center';
 Object.values(batches).flat().forEach(({ sx, sy, titel, relevans }) => {
 if (!titel) return;
 ctx.fillStyle = 'rgba(255,255,255,0.72)';
 ctx.font = '9px Inter,sans-serif';
 ctx.fillText(titel.slice(0, 34), sx, sy + DOT_R + 11);
 });
 }
 // Active node highlights
 if (aktRef.current.length) {
 ctx.fillStyle = '#ffffff88';
 ctx.beginPath();
 aktRef.current.forEach(id => {
 const p = posRef.current.get(id);
 if (!p) return;
 const sx = p.x * k + tx, sy = p.y * k + ty;
 if (sx < -20 || sx > W + 20 || sy < -20 || sy > H + 20) return;
 ctx.arc(sx, sy, 7, 0, Math.PI * 2);
 });
 ctx.fill();
 }
 // Selected node ring
 const sid = selRef.current;
 if (sid) {
 const p = posRef.current.get(sid);
 if (p) {
 const sx = p.x * k + tx, sy = p.y * k + ty;
 if (sx > -20 && sx < W + 20 && sy > -20 && sy < H + 20) {
 ctx.strokeStyle = '#fff'; ctx.lineWidth = 2.5;
 ctx.beginPath(); ctx.arc(sx, sy, 10, 0, Math.PI * 2); ctx.stroke();
 ctx.strokeStyle = '#3fd4c755'; ctx.lineWidth = 1.5;
 ctx.beginPath(); ctx.arc(sx, sy, 18, 0, Math.PI * 2); ctx.stroke();
 }
 }
 }
 };
 const scheduleDraw = () => {
 if (rafRef.current) cancelAnimationFrame(rafRef.current);
 rafRef.current = requestAnimationFrame(() => fnRef.current.doDraw());
 };
 const fitAll = () => {
 const cv = cvRef.current, bbox = bboxRef.current;
 if (!cv || !bbox || !cv.width) return;
 const W = cv.width, H = cv.height;
 const pw = bbox.maxX - bbox.minX + 400, ph = bbox.maxY - bbox.minY + 400;
 const k = Math.min(W/pw, H/ph) * 0.88;
 const cx = (bbox.minX + bbox.maxX)/2, cy = (bbox.minY + bbox.maxY)/2;
 tfRef.current = { k, x: W/2 - cx*k, y: H/2 - cy*k };
 fnRef.current.scheduleDraw();
 };
 const centerOn = (nodeId) => {
 const cv = cvRef.current, p = posRef.current.get(nodeId);
 if (!cv || !p) return;
 const W = cv.width, H = cv.height;
 const k = Math.max(tfRef.current.k, 0.4);
 tfRef.current = { k, x: W/2 - p.x*k, y: H/2 - p.y*k };
 fnRef.current.scheduleDraw();
 };
 fnRef.current = { doDraw, scheduleDraw, fitAll, centerOn };
 useEffect(() => {
 const load = (attempt) =>
 apiFetch('/os/hjerne/alle-noder-graf')
 .then(data => {
 const { posMap, centers, bbox, edgeCoords } = buildCanvasLayout(data.noder);
 posRef.current = posMap; centersRef.current = centers; bboxRef.current = bbox;
 edgesRef.current = edgeCoords;
 setTotal(data.noder.length); setLader(false);
 })
 .catch(e => {
 if (attempt < 2) {
 setTimeout(() => load(attempt + 1), 2000);
 } else {
 setFejl('Fejl: ' + e.message + ' (prøvede 2 gange — genindlæs siden)');
 setLader(false);
 }
 });
 load(1);
 }, []);
 useEffect(() => {
 if (lader || !bboxRef.current) return;
 const ctnr = ctnrRef.current, cv = cvRef.current;
 if (!ctnr || !cv) return;
 cv.width = ctnr.clientWidth; cv.height = ctnr.clientHeight;
 requestAnimationFrame(() => fnRef.current.fitAll());
 const ro = new ResizeObserver(() => {
 cv.width = ctnr.clientWidth; cv.height = ctnr.clientHeight;
 fnRef.current.doDraw();
 });
 ro.observe(ctnr);
 return () => ro.disconnect();
 }, [lader]);
 useEffect(() => {
 if (!selectedNodeId || lader) return;
 selRef.current = selectedNodeId;
 fnRef.current.centerOn(selectedNodeId);
 fnRef.current.scheduleDraw();
 }, [selectedNodeId, lader]);
 useEffect(() => {
 if (!focusNodeId || lader) return;
 fnRef.current.centerOn(focusNodeId);
 fnRef.current.scheduleDraw();
 }, [focusNodeId, lader]);
 useEffect(() => {
 if (lader) return;
 const cv = cvRef.current; if (!cv) return;
 const handler = (e) => {
 e.preventDefault();
 const rect = cv.getBoundingClientRect();
 const mx = e.clientX - rect.left, my = e.clientY - rect.top;
 const { x: tx, y: ty, k } = tfRef.current;
 const f = e.deltaY > 0 ? 0.85 : 1.18;
 const nk = Math.max(0.002, Math.min(30, k * f));
 tfRef.current = { k: nk, x: mx - (mx-tx)*nk/k, y: my - (my-ty)*nk/k };
 fnRef.current.scheduleDraw();
 };
 cv.addEventListener('wheel', handler, { passive: false });
 return () => cv.removeEventListener('wheel', handler);
 }, [lader]);
 const onMD = e => {
 if (e.button !== 0) return;
 dragRef.current = { sx: e.clientX, sy: e.clientY, tx: tfRef.current.x, ty: tfRef.current.y, moved: false };
 };
 const onMM = e => {
 if (!dragRef.current) return;
 const dx = e.clientX - dragRef.current.sx, dy = e.clientY - dragRef.current.sy;
 if (Math.abs(dx) > 2 || Math.abs(dy) > 2) dragRef.current.moved = true;
 tfRef.current = { k: tfRef.current.k, x: dragRef.current.tx + dx, y: dragRef.current.ty + dy };
 fnRef.current.scheduleDraw();
 };
 const onMU = e => {
 if (dragRef.current && !dragRef.current.moved) {
 const cv = cvRef.current; if (!cv) { dragRef.current = null; return; }
 const rect = cv.getBoundingClientRect();
 const mx = e.clientX - rect.left, my = e.clientY - rect.top;
 const { x: tx, y: ty, k } = tfRef.current;
 const wx = (mx-tx)/k, wy = (my-ty)/k;
 const hitR2 = (12/k)**2;
 let best2 = hitR2, bestId = null;
 posRef.current.forEach((p, id) => {
 const d2 = (p.x-wx)**2 + (p.y-wy)**2;
 if (d2 < best2) { best2 = d2; bestId = id; }
 });
 if (bestId) {
 selRef.current = bestId;
 const p = posRef.current.get(bestId);
 const nd = { id: bestId, titel: p.titel, titre: p.titel, gruppe: p.gruppe, type: p.type, sti: p.kilde_sti, kilde_sti: p.kilde_sti };
 setValgt(nd);
 if (onNodeClick) onNodeClick(nd);
 } else {
 selRef.current = null; setValgt(null);
 if (onNodeClick) onNodeClick(null);
 }
 fnRef.current.scheduleDraw();
 }
 dragRef.current = null;
 };
 if (lader) return (
 <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', gap:14, background:'#03060f', borderRadius:12, color:'#4a5a72' }}> <div style={{ width:36, height:36, border:'3px solid #3fd4c720', borderTop:'3px solid #3fd4c7', borderRadius:'50%', animation:'hv-spin 0.8s linear infinite' }} /> <div style={{ fontSize:13 }}>Indlæser alle noder fra databasen…</div> </div>
 );
 if (fejl) return (
 <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', background:'#03060f', borderRadius:12, color:'#ef4444', fontSize:13 }}>{fejl}</div>
 );
 return (
 <div ref={ctnrRef} style={{ position:'relative', width:'100%', height:'100%', background:'#03060f', borderRadius:12, overflow:'hidden' }}> <canvas ref={cvRef} style={{ display:'block', cursor:'grab' }}
 onMouseDown={onMD} onMouseMove={onMM} onMouseUp={onMU}
 onMouseLeave={() => { dragRef.current = null; }} /> <div style={{ position:'absolute', top:10, right:10, display:'flex', gap:6, alignItems:'center' }}> <div style={{ fontSize:11, color:'#3fd4c799', fontFamily:'monospace', background:'rgba(3,6,15,0.8)', padding:'3px 9px', borderRadius:6, pointerEvents:'none' }}>
 {total.toLocaleString('da-DK')} noder
 </div> <button onClick={() => fnRef.current.fitAll()}
 style={{ fontSize:11, color:'#ccc', background:'rgba(3,6,15,0.8)', border:'1px solid #ffffff18', borderRadius:6, padding:'3px 9px', cursor:'pointer' }}>
 ⊙ Reset
 </button> </div> <div style={{ position:'absolute', bottom:10, left:10, display:'flex', flexDirection:'column', gap:4 }}>
 {CANVAS_GRUPPE_ORDER.map(g => {
 const c = centersRef.current[g]; if (!c) return null;
 return (
 <div key={g} style={{ display:'flex', alignItems:'center', gap:5, fontSize:10 }}> <div style={{ width:7, height:7, borderRadius:'50%', background:GRUPPE_FARVER[g]||'#94a3b8', flexShrink:0 }} /> <span style={{ color:GRUPPE_FARVER[g]||'#94a3b8', fontWeight:700 }}>{GRUPPE_LABELS[g]||g}</span> <span style={{ color:'#4a5a72' }}>{c.count.toLocaleString('da-DK')}</span> </div>
 );
 })}
 </div>
 {valgt && (
 <div style={{ position:'absolute', bottom:10, right:10, maxWidth:260, background:'rgba(3,12,21,0.92)', border:'1px solid #ffffff18', borderRadius:10, padding:'10px 14px', backdropFilter:'blur(10px)' }}> <button onClick={() => { selRef.current=null; setValgt(null); if(onNodeClick) onNodeClick(null); fnRef.current.scheduleDraw(); }}
 style={{ position:'absolute', top:5, right:8, background:'none', border:'none', color:'#4a5a72', cursor:'pointer', fontSize:14, lineHeight:1 }}></button> <div style={{ fontSize:10, fontWeight:700, color:GRUPPE_FARVER[valgt.gruppe]||'#3fd4c7', textTransform:'uppercase', letterSpacing:0.8, marginBottom:4 }}>
 {GRUPPE_LABELS[valgt.gruppe]||valgt.gruppe}
 </div> <div style={{ fontSize:12, color:'#e2e8f0', lineHeight:1.45, marginBottom:3, paddingRight:16 }}>{valgt.titel}</div> <div style={{ fontSize:10, color:'#4a5a72' }}>
 {valgt.type}{valgt.kilde_sti ? ' · '+valgt.kilde_sti.split('/').pop() : ''}
 </div> </div>
 )}
 </div>
 );
}
// ── Knowledge Graph (D3 force simulation) ─────────────────
function KnowledgeGraph({ graph, indlæser, aktiveAgenter = [], aktiveNodeIds = [], onNodeClick, focusNodeId, liveStats }) {
 const svgRef = useRef(null);
 const simRef = useRef(null);
 const noderRef = useRef(null);
 const zoomBehaviorRef = useRef(null);
 const onNodeClickRef = useRef(onNodeClick);
 const gRef = useRef(null); // D3 zoom-gruppe
 const dokOverlayRef = useRef(null); // D3 gruppe til dok-noder
 const dokStateRef = useRef(null); // Aktiv dok-tilstand til tick-handler
 const [valgt, setValgt] = useState(null);
 const [tooltip, setTooltip] = useState(null);
 const [zoomLevel, setZoomLevel] = useState(1);
 const [dokState, setDokState] = useState(null); // { sagId, sagNodeId, docs }
 useEffect(() => { onNodeClickRef.current = onNodeClick; }, [onNodeClick]);
 // Synkroniser dokState til ref (tick-handler kan ikke se state)
 useEffect(() => { dokStateRef.current = dokState; }, [dokState]);
 // Udfolde dok-noder ved zoom + valgt klientsager-node
 useEffect(() => {
 if (!valgt || zoomLevel < ZOOM_DOK_THRESHOLD) { setDokState(null); return; }
 const node = noderRef.current?.find(n => n.id === valgt);
 if (!node || node.gruppe !== 'klientsager' || !node.sag_id) { setDokState(null); return; }
 apiFetch(`/os/hjerne/sag/${node.sag_id}/dokumenter`)
 .then(docs => setDokState({ sagId: node.sag_id, sagNodeId: valgt, docs: docs.slice(0, 32) }))
 .catch(() => setDokState(null));
 }, [valgt, zoomLevel]);
 // Tegn/opdater dok-overlay når dokState ændres
 useEffect(() => {
 const overlay = dokOverlayRef.current;
 if (!overlay) return;
 overlay.selectAll('*').remove();
 if (!dokState?.docs?.length) return;
 const sagNode = noderRef.current?.find(n => n.id === dokState.sagNodeId);
 if (!sagNode || sagNode.x === undefined) return;
 const docs = dokState.docs;
 const ringR = Math.min(55 + docs.length * 1.8, 95);
 const cx = sagNode.x, cy = sagNode.y;
 docs.forEach((doc, i) => {
 const angle = (i / docs.length) * 2 * Math.PI - Math.PI / 2;
 const x = cx + Math.cos(angle) * ringR;
 const y = cy + Math.sin(angle) * ringR;
 const grp = overlay.append('g').attr('class', 'dok-group');
 grp.append('line').attr('class', 'dok-line')
 .attr('x1', cx).attr('y1', cy).attr('x2', x).attr('y2', y)
 .attr('stroke', '#15803d').attr('stroke-opacity', 0.35)
 .attr('stroke-width', 0.6).attr('stroke-dasharray', '3,3');
 grp.append('circle').attr('class', 'dok-node')
 .attr('cx', x).attr('cy', y).attr('r', 3.5)
 .attr('fill', '#4ade80').attr('fill-opacity', 0.82)
 .attr('filter', 'url(#glow-sagsdokumenter)');
 const lbl = doc.fil_navn.length > 14 ? doc.fil_navn.slice(0, 12) + '…' : doc.fil_navn;
 grp.append('text').attr('class', 'dok-text')
 .attr('x', x).attr('y', y + 9)
 .attr('text-anchor', 'middle')
 .attr('fill', '#4ade80').attr('fill-opacity', 0.65)
 .attr('font-size', 6).attr('font-family', '"Inter",sans-serif')
 .text(lbl);
 });
 }, [dokState]);
 useEffect(() => {
 if (!graph?.nodes?.length || !svgRef.current) return;
 const el = svgRef.current;
 const container = el.parentElement;
 const W = (container?.clientWidth || el.clientWidth || 800);
 const H = (container?.clientHeight || el.clientHeight || 520);
 d3.select(el).selectAll('*').remove();
 const svg = d3.select(el)
 .attr('width', W)
 .attr('height', H)
 .attr('viewBox', `0 0 ${W} ${H}`)
 .style('background', '#03060f')
 .style('border-radius', '12px');
 // Glow-filtre
 const defs = svg.append('defs');
 Object.entries(GRUPPE_FARVER).forEach(([gruppe, farve]) => {
 const filter = defs.append('filter').attr('id', `glow-${gruppe}`);
 filter.append('feGaussianBlur').attr('stdDeviation', 3).attr('result', 'blur');
 const merge = filter.append('feMerge');
 merge.append('feMergeNode').attr('in', 'blur');
 merge.append('feMergeNode').attr('in', 'SourceGraphic');
 });
 const g = svg.append('g');
 gRef.current = g;
 // Zoom — tracker zoom-niveau til dok-overlay
 const zoom = d3.zoom()
 .scaleExtent([0.15, 6])
 .on('zoom', ev => {
 g.attr('transform', ev.transform);
 setZoomLevel(Math.round(ev.transform.k * 10) / 10);
 });
 svg.call(zoom);
 zoomBehaviorRef.current = zoom;
 // Overlay til zoom-baserede dokumentnoder (tilføjes sidst i g så de er øverst)
 const dokOverlayG = g.append('g').attr('id', 'dok-overlay');
 dokOverlayRef.current = dokOverlayG;
 const kanter = (graph.edges || []).filter(e =>
 graph.nodes.find(n => n.id === (typeof e.source === 'string' ? e.source : e.source?.id)) &&
 graph.nodes.find(n => n.id === (typeof e.target === 'string' ? e.target : e.target?.id))
 );
 const noder = graph.nodes.map(n => ({ ...n, gruppe: deriveGroupe(n) }));
 // Beregn degree per node (antal forbindelser)
 const degreeMap = {};
 noder.forEach(n => { degreeMap[n.id] = 0; });
 kanter.forEach(e => {
 const src = typeof e.source === 'string' ? e.source : e.source?.id;
 const tgt = typeof e.target === 'string' ? e.target : e.target?.id;
 if (degreeMap[src] !== undefined) degreeMap[src]++;
 if (degreeMap[tgt] !== undefined) degreeMap[tgt]++;
 });
 const maxDeg = Math.max(1, ...Object.values(degreeMap));
 const nodeRadius = d => {
 if (d.gruppe === 'phantom') return 8;
 const deg = degreeMap[d.id] || 0;
 return 6 + (deg / maxDeg) * 16;
 };
 // Simulation — stærk centrering + spread
 const sim = d3.forceSimulation(noder)
 .force('link', d3.forceLink(kanter.map(e => ({...e}))).id(d => d.id).distance(80).strength(0.4))
 .force('charge', d3.forceManyBody().strength(-250).distanceMax(400))
 .force('center', d3.forceCenter(W / 2, H / 2).strength(0.08))
 .force('x', d3.forceX(W / 2).strength(0.04))
 .force('y', d3.forceY(H / 2).strength(0.04))
 .force('collision', d3.forceCollide().radius(d => nodeRadius(d) + 8))
 .alphaDecay(0.015)
 .velocityDecay(0.4);
 simRef.current = sim;
 noderRef.current = noder;
 // Kanter
 const link = g.append('g').selectAll('line')
 .data(kanter)
 .enter().append('line')
 .attr('stroke', 'rgba(200,220,255,0.22)')
 .attr('stroke-width', 1);
 // Glow-cirkler bag noder
 const glow = g.append('g').selectAll('circle')
 .data(noder)
 .enter().append('circle')
 .attr('r', d => nodeRadius(d) + 6)
 .attr('fill', d => GRUPPE_FARVER[d.gruppe] || GRUPPE_FARVER.other)
 .attr('fill-opacity', 0.07)
 .attr('stroke', 'none');
 // Node-grupper
 const nodeG = g.append('g').selectAll('g')
 .data(noder)
 .enter().append('g')
 .attr('class', 'node')
 .style('cursor', 'pointer')
 .on('mouseover', (ev, d) => {
 const rect = el.getBoundingClientRect();
 setTooltip({ id: d.id, label: d.label, gruppe: d.gruppe, sti: d.sti, x: ev.clientX - rect.left, y: ev.clientY - rect.top });
 })
 .on('mouseout', () => setTooltip(null))
 .on('click', (ev, d) => {
 ev.stopPropagation();
 setValgt(d.id);
 if (onNodeClickRef.current) onNodeClickRef.current(d);
 })
 .call(d3.drag()
 .on('start', (ev, d) => { if (!ev.active) sim.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y; })
 .on('drag', (ev, d) => { d.fx = ev.x; d.fy = ev.y; })
 .on('end', (ev, d) => { if (!ev.active) sim.alphaTarget(0); d.fx = null; d.fy = null; })
 );
 // Hjerne-fil-node detektor (har sti, men ikke en firma_viden type)
 const isFilNode = d => d.sti && !(d.type && NODE_TYPE_LABELS[d.type]);
 // Cirkler
 nodeG.append('circle')
 .attr('r', nodeRadius)
 .attr('fill', d => GRUPPE_FARVER[d.gruppe] || GRUPPE_FARVER.other)
 .attr('fill-opacity', d => {
 if (d.gruppe === 'phantom') return 0.65;
 if (isFilNode(d)) return 0.30; // hjerne-fil: tynd/gennemsigtig
 return 0.88;
 })
 .attr('stroke', d => GRUPPE_FARVER[d.gruppe] || GRUPPE_FARVER.other)
 .attr('stroke-width', d => isFilNode(d) ? 1.5 : 1.5)
 .attr('stroke-opacity', 0.75)
 .attr('stroke-dasharray', d => isFilNode(d) ? '3,2' : 'none')
 .attr('filter', d => `url(#glow-${d.gruppe})`);
 // Lille dokument-ikon (foldet hjørne) for hjerne-fil-noder
 nodeG.filter(d => isFilNode(d))
 .append('path')
 .attr('d', d => {
 const s = nodeRadius(d) * 0.32;
 return `M ${-s},${-s*1.2} L ${s*0.5},${-s*1.2} L ${s},${-s*0.6} L ${s},${s*1.2} L ${-s},${s*1.2} Z M ${s*0.5},${-s*1.2} L ${s*0.5},${-s*0.6} L ${s},${-s*0.6}`;
 })
 .attr('fill', 'none')
 .attr('stroke', d => GRUPPE_FARVER[d.gruppe] || GRUPPE_FARVER.other)
 .attr('stroke-width', 0.7)
 .attr('stroke-opacity', 0.7)
 .style('pointer-events', 'none');
 // Labels — altid synlige, gruppe-farvet, med blød hover-effekt via CSS opacity
 nodeG.append('text')
 .text(d => {
 const lbl = d.label || d.id;
 return lbl.length > 20 ? lbl.slice(0, 18) + '…' : lbl;
 })
 .attr('x', 0)
 .attr('y', d => nodeRadius(d) + 11)
 .attr('text-anchor', 'middle')
 .attr('fill', d => GRUPPE_FARVER[d.gruppe] || GRUPPE_FARVER.other)
 .attr('fill-opacity', 0.75)
 .attr('font-size', 9)
 .attr('font-weight', '500')
 .attr('font-family', '"Inter",sans-serif')
 .style('pointer-events', 'none');
 // Tick
 sim.on('tick', () => {
 link
 .attr('x1', d => d.source.x).attr('y1', d => d.source.y)
 .attr('x2', d => d.target.x).attr('y2', d => d.target.y);
 glow.attr('cx', d => d.x).attr('cy', d => d.y);
 nodeG.attr('transform', d => `translate(${d.x},${d.y})`);
 // Repositionér dok-overlay rundt om sag-node
 const ds = dokStateRef.current;
 if (ds?.docs?.length && dokOverlayRef.current) {
 const sn = noderRef.current?.find(n => n.id === ds.sagNodeId);
 if (sn && sn.x !== undefined) {
 const docs = ds.docs;
 const ringR = Math.min(55 + docs.length * 1.8, 95);
 dokOverlayRef.current.selectAll('.dok-group').each(function(_, i) {
 const angle = (i / docs.length) * 2 * Math.PI - Math.PI / 2;
 const x = sn.x + Math.cos(angle) * ringR;
 const y = sn.y + Math.sin(angle) * ringR;
 const grp = d3.select(this);
 grp.select('.dok-line').attr('x1', sn.x).attr('y1', sn.y).attr('x2', x).attr('y2', y);
 grp.select('.dok-node').attr('cx', x).attr('cy', y);
 grp.select('.dok-text').attr('x', x).attr('y', y + 9);
 });
 }
 }
 });
 svg.on('click', () => {
 setValgt(null);
 if (onNodeClickRef.current) onNodeClickRef.current(null);
 });
 return () => { sim.stop(); };
 }, [graph]);
 // Fremhæv valgt node
 useEffect(() => {
 if (!svgRef.current) return;
 d3.select(svgRef.current).selectAll('.node circle')
 .attr('stroke-opacity', function(d) { return valgt && d.id !== valgt ? 0.2 : 0.6; })
 .attr('fill-opacity', function(d) {
 if (d.gruppe === 'phantom') return valgt && d.id !== valgt ? 0.15 : 0.65;
 return valgt && d.id !== valgt ? 0.2 : 0.85;
 });
 d3.select(svgRef.current).selectAll('line')
 .attr('stroke-opacity', function(d) {
 if (!valgt) return 1;
 const src = d.source?.id || d.source;
 const tgt = d.target?.id || d.target;
 return (src === valgt || tgt === valgt) ? 1 : 0.06;
 })
 .attr('stroke', function(d) {
 if (!valgt) return 'rgba(200,220,255,0.22)';
 const src = d.source?.id || d.source;
 const tgt = d.target?.id || d.target;
 return (src === valgt || tgt === valgt) ? 'rgba(220,240,255,0.7)' : 'rgba(200,220,255,0.06)';
 });
 }, [valgt]);
 // Live puls: grønt på noder tilhørende aktive agenter ELLER aktivt forespurgte videns-noder
 useEffect(() => {
 if (!svgRef.current) return;
 d3.select(svgRef.current).selectAll('.node').each(function(d) {
 const agentId = deriveAgentId(d.sti);
 const isAgentAktiv = agentId && aktiveAgenter.includes(agentId);
 const isVidenAktiv = aktiveNodeIds.includes(d.id);
 const isActive = isAgentAktiv || isVidenAktiv;
 const farve = GRUPPE_FARVER[d.gruppe] || GRUPPE_FARVER.other;
 // Videns-noder (firma_viden_noder) vises med cyan-farve, agent-noder med grøn
 const pulseFarve = isVidenAktiv ? '#3fd4c7' : '#22c55e';
 d3.select(this).select('circle')
 .attr('stroke', isActive ? pulseFarve : farve)
 .attr('stroke-width', isActive ? (isVidenAktiv ? 4 : 3) : 1.5)
 .style('animation', isActive ? 'hv-agent-ring 1.4s ease-in-out infinite' : 'none');
 d3.select(this).select('text')
 .attr('fill', isActive ? (isVidenAktiv ? '#3fd4c7' : '#4ade80') : farve)
 .attr('fill-opacity', isActive ? 1 : 0.75)
 .attr('font-weight', isActive ? '700' : '500');
 });
 }, [aktiveAgenter, aktiveNodeIds]);
 // Zoom + fremhæv node (fra klik på "Forbundne" i FilPanel)
 useEffect(() => {
 if (!focusNodeId || !svgRef.current || !noderRef.current || !zoomBehaviorRef.current) return;
 const node = noderRef.current.find(n => n.id === focusNodeId);
 if (!node || node.x === undefined) return;
 setValgt(focusNodeId);
 const el = svgRef.current;
 const W = el.clientWidth || 800;
 const H = el.clientHeight || 520;
 const scale = 2;
 const tx = W / 2 - node.x * scale;
 const ty = H / 2 - node.y * scale;
 d3.select(el).transition().duration(600)
 .call(zoomBehaviorRef.current.transform, d3.zoomIdentity.translate(tx, ty).scale(scale));
 }, [focusNodeId]);
 const gruppe_farver = Object.entries(GRUPPE_FARVER);
 const gruppeAntal = (gruppe) =>
 (graph?.nodes || []).filter(n => deriveGroupe(n) === gruppe).length;
 // Live DB-totaler per gruppe (alle node_types korrekt mappet — fra gruppe_totaler i stats)
 const liveGruppe = liveStats?.gruppe_totaler || null;
 return (
 <div style={{ position: 'relative', width: '100%', height: '100%' }}> <svg ref={svgRef} style={{ width: '100%', height: '100%', borderRadius: 12 }} />
 {indlæser && (
 <div style={{
 position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
 background: 'rgba(3,6,15,0.7)', borderRadius: 12,
 }}> <div style={{ textAlign: 'center', color: '#3fd4c7', fontSize: 13 }}> <div style={{ width: 28, height: 28, border: '2px solid rgba(63,212,199,0.2)', borderTop: '2px solid #3fd4c7', borderRadius: '50%', animation: 'hv-spin 0.8s linear infinite', margin: '0 auto 10px' }} />
 Bygger graf…
 </div> </div>
 )}
 {/* Tooltip */}
 {tooltip && (
 <div style={{
 position: 'absolute', left: Math.min(tooltip.x + 12, 600), top: tooltip.y - 10,
 background: 'rgba(4,8,20,0.96)', backdropFilter: 'blur(12px)',
 border: `1px solid ${GRUPPE_FARVER[tooltip.gruppe] || '#3fd4c7'}30`,
 borderRadius: 8, padding: '8px 12px', pointerEvents: 'none', zIndex: 10,
 animation: 'hv-fadein 0.15s ease',
 maxWidth: 220,
 }}> <div style={{ color: GRUPPE_FARVER[tooltip.gruppe] || '#3fd4c7', fontWeight: 700, fontSize: 11 }}>
 {tooltip.label || tooltip.id}
 </div>
 {tooltip.sti && (
 <div style={{ color: '#4a5a72', fontSize: 10, marginTop: 3, fontFamily: 'monospace' }}>
 {tooltip.sti}
 </div>
 )}
 <div style={{ marginTop: 4, padding: '2px 6px', borderRadius: 4, background: `${GRUPPE_FARVER[tooltip.gruppe] || '#3fd4c7'}15`, display: 'inline-block', fontSize: 10, color: GRUPPE_FARVER[tooltip.gruppe] || '#3fd4c7' }}>
 {GRUPPE_LABELS[tooltip.gruppe] || tooltip.gruppe}
 </div> </div>
 )}
 {/* Legende */}
 <div style={{
 position: 'absolute', bottom: 36, left: 12,
 display: 'flex', flexDirection: 'column', gap: 4,
 background: 'rgba(4,8,20,0.88)', backdropFilter: 'blur(10px)',
 borderRadius: 10, padding: '10px 12px 10px',
 border: '1px solid rgba(255,255,255,0.07)',
 minWidth: 160,
 }}> <div style={{ fontSize: 9, color: '#3a4a5a', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>
 Grupper
 </div>
 {gruppe_farver.map(([gruppe, farve]) => {
 const snapAntal = gruppeAntal(gruppe);
 if (!snapAntal) return null;
 const liveAntal = liveGruppe?.[gruppe];
 return (
 <div key={gruppe} style={{ display: 'flex', alignItems: 'center', gap: 6 }}> <div style={{ width: 8, height: 8, borderRadius: '50%', background: farve, boxShadow: `0 0 6px ${farve}90`, flexShrink: 0 }} /> <span style={{ fontSize: 10, color: '#7a8a9a' }}>{GRUPPE_LABELS[gruppe]}</span> <div style={{ marginLeft: 'auto', paddingLeft: 6, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0 }}>
 {liveAntal != null ? (
 <> <span style={{ fontSize: 10, color: farve + 'cc', fontWeight: 700 }}>{liveAntal.toLocaleString('da-DK')}</span> <span style={{ fontSize: 8, color: '#2a3a4a', lineHeight: 1 }}>{snapAntal} i graf</span> </>
 ) : (
 <span style={{ fontSize: 10, color: farve + 'cc' }}>{snapAntal}</span>
 )}
 </div> </div>
 );
 })}
 {liveStats && (
 <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid rgba(255,255,255,0.06)', fontSize: 9, color: '#2a3a4a' }}>
 Total i DB: <span style={{ color: '#3fd4c7', fontWeight: 700 }}>{(liveStats.firmaviden || 0).toLocaleString('da-DK')}</span> </div>
 )}
 </div>
 {/* Live agent-aktivitet */}
 <div style={{
 position: 'absolute', top: 12, right: 12,
 background: 'rgba(4,8,20,0.85)', backdropFilter: 'blur(10px)',
 borderRadius: 10, padding: '10px 12px',
 border: '1px solid rgba(255,255,255,0.07)',
 minWidth: 150,
 }}> <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: aktiveAgenter.length ? 8 : 0 }}> <div style={{
 width: 7, height: 7, borderRadius: '50%',
 background: aktiveAgenter.length ? '#22c55e' : '#2a3a4a',
 boxShadow: aktiveAgenter.length ? '0 0 8px #22c55e' : 'none',
 animation: aktiveAgenter.length ? 'hv-live-dot 1.2s ease infinite' : 'none',
 flexShrink: 0,
 }} /> <span style={{ fontSize: 10, color: aktiveAgenter.length ? '#4ade80' : '#3a4a5a', fontWeight: 700 }}>
 {aktiveAgenter.length ? `${aktiveAgenter.length} aktiv${aktiveAgenter.length > 1 ? 'e' : ''}` : 'Ingen aktive'}
 </span> </div>
 {aktiveAgenter.map(aid => (
 <div key={aid} style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 4 }}> <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} /> <span style={{ fontSize: 9, color: '#4ade80', fontFamily: 'monospace' }}>{aid}</span> </div>
 ))}
 </div>
 {/* Zoom-hint */}
 <div style={{
 position: 'absolute', bottom: 12, right: 12,
 fontSize: 9, color: '#2a3a4a', fontFamily: 'monospace',
 }}>
 Scroll = zoom · Drag = pan · Klik = fremhæv{zoomLevel >= ZOOM_DOK_THRESHOLD ? ` · Zoom ${zoomLevel.toFixed(1)}x dok synlige` : ` · Zoom ${zoomLevel.toFixed(1)}x`}
 </div> </div>
 );
}
// ── Seneste filer ──────────────────────────────────────────
function SenesteFiler({ filer }) {
 if (!filer?.length) return (
 <div style={{ color: '#4a5a72', fontSize: 12, padding: '20px 0', textAlign: 'center' }}>
 Ingen data endnu — kør hjerne-stats-sync.ps1
 </div>
 );
 return (
 <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
 {filer.map((fil, i) => {
 const gruppe = deriveGroupe({ sti: fil.sti, gruppe: 'other' });
 const farve = GRUPPE_FARVER[gruppe] || GRUPPE_FARVER.other;
 return (
 <div key={i} style={{
 padding: '12px 14px',
 background: `linear-gradient(135deg, ${farve}08, rgba(4,8,20,0.6))`,
 border: `1px solid ${farve}20`,
 borderRadius: 10,
 animation: `hv-fadein 0.4s ease ${i * 0.07}s both`,
 }}> <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}> <div style={{ color: farve, fontWeight: 700, fontSize: 12 }}>
 {fil.navn?.replace(/-/g, ' ')}
 </div> <div style={{ fontSize: 10, color: '#4a5a72', fontFamily: 'monospace', whiteSpace: 'nowrap', flexShrink: 0 }}>
 {fil.dato}
 </div> </div>
 {fil.sti && (
 <div style={{ fontSize: 10, color: '#3a4a5a', fontFamily: 'monospace', marginTop: 2 }}>
 {fil.sti}
 </div>
 )}
 {fil.preview && (
 <div style={{ marginTop: 6, fontSize: 11, color: '#5a6a82', lineHeight: 1.5 }}>
 {fil.preview}
 </div>
 )}
 </div>
 );
 })}
 </div>
 );
}
// ── Mappe-browser (hierarchical folder drilldown) ─────────
function MappeBrowser() {
 const [sti, setSti] = useState('');
 const [mappeData, setMappeData] = useState(null);
 const [mappeError, setMappeError] = useState(null);
 const [loading, setLoading] = useState(false);
 const [valgtFil, setValgtFil] = useState(null);
 const [filIndhold, setFilIndhold] = useState(null);
 const [filLader, setFilLader] = useState(false);
 const hentMappe = useCallback(async (nyeSti) => {
 setLoading(true);
 setMappeError(null);
 setValgtFil(null); setFilIndhold(null);
 try {
 const qs = nyeSti ? '?sti=' + encodeURIComponent(nyeSti) : '';
 const r = await apiFetch('/os/hjerne/mappe' + qs);
 setMappeData(r);
 setSti(nyeSti);
 } catch (e) {
 setMappeData(null);
 setMappeError(e.message || 'Ukendt fejl');
 }
 finally { setLoading(false); }
 }, []);
 useEffect(() => { hentMappe(''); }, [hentMappe]);
 const hentFil = useCallback(async (filSti) => {
 setValgtFil(filSti); setFilIndhold(null); setFilLader(true);
 try {
 const r = await apiFetch('/os/hjerne/fil?sti=' + encodeURIComponent(filSti));
 setFilIndhold(r);
 } catch (_) { setFilIndhold(null); }
 finally { setFilLader(false); }
 }, []);
 // Breadcrumbs fra sti
 const breadcrumbs = useMemo(() => {
 if (!sti) return [];
 const parts = sti.split('/');
 return parts.map((p, i) => ({ label: p, sti: parts.slice(0, i + 1).join('/') }));
 }, [sti]);
 const MAPPER_FARVE = '#60a5fa';
 const FIL_FARVE = '#94a3b8';
 return (
 <div style={{ display: 'flex', gap: 12, height: 'calc(100vh - 320px)', minHeight: 420, animation: 'hv-fadein 0.3s ease' }}>
 {/* Venstre: mappevisning */}
 <div style={{
 width: 300, flexShrink: 0,
 background: 'rgba(4,8,20,0.8)', border: '1px solid rgba(255,255,255,0.07)',
 borderRadius: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden',
 }}>
 {/* Header + breadcrumb */}
 <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}> <div style={{ fontSize: 11, fontWeight: 700, color: MAPPER_FARVE, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
 Hjernen/ mapper
 </div>
 {/* Breadcrumb */}
 <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', fontSize: 10, color: '#4a5a72' }}> <button onClick={() => hentMappe('')} style={{
 background: 'none', border: 'none', cursor: 'pointer', padding: '1px 4px',
 color: sti ? '#4a5a72' : MAPPER_FARVE, fontWeight: sti ? 400 : 700,
 borderRadius: 4, fontSize: 10,
 }}>hjernen</button>
 {breadcrumbs.map((bc, i) => (
 <React.Fragment key={bc.sti}> <span style={{ color: '#2a3a4a' }}>/</span> <button onClick={() => hentMappe(bc.sti)} style={{
 background: 'none', border: 'none', cursor: 'pointer', padding: '1px 4px',
 color: i === breadcrumbs.length - 1 ? MAPPER_FARVE : '#4a5a72',
 fontWeight: i === breadcrumbs.length - 1 ? 700 : 400,
 borderRadius: 4, fontSize: 10,
 }}>{bc.label}</button> </React.Fragment>
 ))}
 </div> </div>
 {/* Liste */}
 <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
 {loading && (
 <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 16px', color: '#3a4a5a', fontSize: 11 }}> <div style={{ width: 12, height: 12, border: `1.5px solid ${MAPPER_FARVE}30`, borderTop: `1.5px solid ${MAPPER_FARVE}`, borderRadius: '50%', animation: 'hv-spin 0.8s linear infinite', flexShrink: 0 }} />
 Henter…
 </div>
 )}
 {!loading && mappeError && (
 <div style={{ padding: '14px 16px', color: '#f87171', fontSize: 11, lineHeight: 1.5 }}>
 Fejl: {mappeError}
 <button onClick={() => hentMappe(sti)} style={{
 display: 'block', marginTop: 8,
 background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)',
 color: '#f87171', borderRadius: 6, padding: '4px 10px',
 cursor: 'pointer', fontSize: 10,
 }}>Prøv igen</button> </div>
 )}
 {!loading && sti && (
 <button onClick={() => hentMappe(breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 2].sti : '')} style={{
 display: 'flex', alignItems: 'center', gap: 8,
 width: '100%', padding: '7px 16px',
 background: 'none', border: 'none', cursor: 'pointer',
 color: '#4a5a72', fontSize: 11, textAlign: 'left',
 transition: 'background 0.15s',
 }}
 onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
 onMouseLeave={e => e.currentTarget.style.background = 'none'}
 > <span style={{ fontSize: 13 }}>↩</span> <span style={{ fontFamily: 'monospace' }}>..</span> </button>
 )}
 {!loading && (mappeData?.children || []).map(entry => {
 const erMappe = entry.type === 'mappe';
 const erValgt = !erMappe && valgtFil === entry.sti;
 const farve = erMappe ? MAPPER_FARVE : FIL_FARVE;
 return (
 <button
 key={entry.sti}
 onClick={() => erMappe ? hentMappe(entry.sti) : hentFil(entry.sti)}
 style={{
 display: 'flex', alignItems: 'center', gap: 8,
 width: '100%', padding: '7px 16px',
 background: erValgt ? `${farve}12` : 'none',
 border: 'none', cursor: 'pointer', color: erValgt ? farve : farve + 'aa',
 fontSize: 11, textAlign: 'left', transition: 'background 0.15s',
 borderLeft: erValgt ? `2px solid ${farve}` : '2px solid transparent',
 }}
 onMouseEnter={e => { if (!erValgt) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
 onMouseLeave={e => { if (!erValgt) e.currentTarget.style.background = 'none'; }}
 > <span style={{ flexShrink: 0, fontSize: 13 }}>{erMappe ? '' : ''}</span> <span style={{ flex: 1, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
 {entry.navn}
 </span>
 {erMappe && (
 <span style={{ flexShrink: 0, fontSize: 9, color: farve + '70', fontFamily: 'monospace', marginLeft: 4 }}>
 {entry.antal}
 </span>
 )}
 </button>
 );
 })}
 {!loading && mappeData?.antal === 0 && (
 <div style={{ padding: '16px', color: '#3a4a5a', fontSize: 11, textAlign: 'center' }}>
 Tom mappe
 </div>
 )}
 </div>
 {/* Footer */}
 {mappeData && (
 <div style={{ padding: '8px 16px', borderTop: '1px solid rgba(255,255,255,0.05)', fontSize: 10, color: '#2a3a4a', fontFamily: 'monospace' }}>
 {mappeData.antal} {mappeData.antal === 1 ? 'element' : 'elementer'}
 </div>
 )}
 </div>
 {/* Højre: filindhold */}
 <div style={{
 flex: 1, minWidth: 0,
 background: 'rgba(4,8,20,0.8)', border: '1px solid rgba(255,255,255,0.07)',
 borderRadius: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden',
 }}>
 {!valgtFil && (
 <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8, color: '#2a3a4a', fontSize: 12 }}> <div style={{ fontSize: 32 }}></div>
 Vælg en fil for at se indholdet
 </div>
 )}
 {valgtFil && (
 <> <div style={{ padding: '14px 18px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}> <div style={{ fontSize: 12, fontWeight: 700, color: FIL_FARVE, fontFamily: 'monospace', wordBreak: 'break-all' }}>
 {valgtFil}
 </div> </div> <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
 {filLader && (
 <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#3a4a5a', fontSize: 11 }}> <div style={{ width: 12, height: 12, border: `1.5px solid ${FIL_FARVE}30`, borderTop: `1.5px solid ${FIL_FARVE}`, borderRadius: '50%', animation: 'hv-spin 0.8s linear infinite', flexShrink: 0 }} />
 Henter fil…
 </div>
 )}
 {!filLader && filIndhold?.indhold ? (
 <pre style={{
 margin: 0, fontSize: 11, lineHeight: 1.7,
 color: '#c8d8e8', fontFamily: '"JetBrains Mono","Fira Code",monospace',
 whiteSpace: 'pre-wrap', wordBreak: 'break-word',
 }}>
 {filIndhold.indhold}
 </pre>
 ) : !filLader && (
 <div style={{ color: '#3a4a5a', fontSize: 11 }}>Ingen indhold fundet</div>
 )}
 </div> </>
 )}
 </div> </div>
 );
}
// ── IndholdsoversigtsVisning ─────────────────────────────
const OV_GRUPPE_FARVER = {
 virksomhed: '#3fd4c7',
 laering: '#8b5cf6',
 sager: '#60a5fa',
 beslutninger: '#f59e0b',
 agenter: '#ec4899',
 scripts: '#94a3b8',
};
function IndholdsoversigtsVisning() {
 const [data, setData] = useState(null);
 const [lader, setLader] = useState(true);
 const [fejl, setFejl] = useState(null);
 const [valgt, setValgt] = useState(null);
 const [dokNoder, setDokNoder] = useState([]);
 const [dokTotal, setDokTotal] = useState(0);
 const [dokOffset, setDokOffset] = useState(0);
 const [dokLader, setDokLader] = useState(false);
 useEffect(() => {
 fetch('/api/os/hjerne/indholdsoversigt', { credentials: 'include' })
 .then(r => r.ok ? r.json() : Promise.reject('HTTP ' + r.status))
 .then(d => { setData(d); setLader(false); })
 .catch(e => { setFejl(String(e)); setLader(false); });
 }, []);
 const hentDok = useCallback((nodeType, off = 0) => {
 setDokLader(true);
 fetch(`/api/os/hjerne/dok-liste?node_type=${encodeURIComponent(nodeType)}&limit=50&offset=${off}`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : Promise.reject())
 .then(d => {
 setDokNoder(prev => off === 0 ? d.noder : [...prev, ...d.noder]);
 setDokTotal(d.total);
 setDokOffset(off);
 setDokLader(false);
 })
 .catch(() => setDokLader(false));
 }, []);
 const vælgKategori = (kat) => {
 if (valgt?.node_type === kat.node_type) {
 setValgt(null); setDokNoder([]); setDokTotal(0);
 } else {
 setValgt(kat); setDokNoder([]); setDokTotal(0); setDokOffset(0);
 hentDok(kat.node_type, 0);
 }
 };
 if (lader) return (
 <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: '#4a5a72', gap: 10 }}> <div style={{ width: 18, height: 18, border: '2px solid rgba(63,212,199,0.2)', borderTop: '2px solid #3fd4c7', borderRadius: '50%', animation: 'hv-spin 0.8s linear infinite' }} /> <span style={{ fontSize: 13 }}>Henter indholdsoversigt…</span> </div>
 );
 if (fejl) return (
 <div style={{ padding: '14px 18px', borderRadius: 10, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#fca5a5', fontSize: 12 }}>
 {fejl}
 </div>
 );
 if (!data) return null;
 const totalDok = data.dokumenter.reduce((s, d) => s + d.antal, 0);
 const totalFil = data.hjerne_filer.reduce((s, d) => s + d.antal, 0);
 const totalKon = data.konceptuelle.reduce((s, d) => s + d.antal, 0);
 const sektStyle = {
 background: 'rgba(8,12,30,0.5)',
 border: '1px solid rgba(255,255,255,0.06)',
 borderRadius: 12,
 padding: '16px 18px',
 marginBottom: 14,
 };
 const KatKort = ({ kat, farve, aktiv, onClick }) => (
 <div onClick={onClick} style={{
 padding: '11px 13px', borderRadius: 10, cursor: 'pointer',
 background: aktiv ? `${farve}18` : 'rgba(8,12,30,0.6)',
 border: `1px solid ${aktiv ? farve + '55' : 'rgba(255,255,255,0.06)'}`,
 transition: 'all 0.18s',
 }}> <div style={{ fontSize: 18, fontWeight: 800, color: farve, fontFamily: 'monospace', lineHeight: 1 }}>
 {kat.antal.toLocaleString('da-DK')}
 </div> <div style={{ fontSize: 10, color: aktiv ? '#c8d8e8' : '#5a6a7a', marginTop: 4, lineHeight: 1.3, wordBreak: 'break-word' }}>
 {kat.node_type || kat.kategori || kat.gruppe}
 </div> </div>
 );
 return (
 <div style={{ animation: 'hv-fadein 0.3s ease' }}>
 {/* Totaler */}
 <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
 {[
 { label: 'Dokumenter & mails', value: totalDok, farve: '#3fd4c7', ikon: '' },
 { label: 'Hjerne-filer (.md)', value: totalFil, farve: '#8b5cf6', ikon: '' },
 { label: 'Konceptuelle noder', value: totalKon, farve: '#f59e0b', ikon: '' },
 { label: 'Total viden', value: totalDok + totalFil + totalKon, farve: '#22c55e', ikon: '' },
 ].map(item => (
 <div key={item.label} style={{
 padding: '10px 16px', borderRadius: 10,
 background: `${item.farve}10`, border: `1px solid ${item.farve}25`,
 display: 'flex', alignItems: 'center', gap: 10,
 }}> <span style={{ fontSize: 20 }}>{item.ikon}</span> <div> <div style={{ fontSize: 22, fontWeight: 800, color: item.farve, fontFamily: 'monospace', lineHeight: 1 }}>
 {item.value.toLocaleString('da-DK')}
 </div> <div style={{ fontSize: 10, color: item.farve + '80', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginTop: 2 }}>
 {item.label}
 </div> </div> </div>
 ))}
 </div> <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
 {/* Venstre: kategorilister */}
 <div style={{ flex: 1, minWidth: 0 }}>
 {/* Dokumenter & mails */}
 <div style={sektStyle}> <div style={{ fontSize: 11, color: '#4a5a72', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 12 }}>
 Dokumenter & mails — {totalDok.toLocaleString('da-DK')} elementer — klik for at se individuelle filer
 </div> <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 }}>
 {data.dokumenter.map(kat => (
 <KatKort
 key={kat.kategori}
 kat={{ ...kat, node_type: kat.kategori }}
 farve={OV_GRUPPE_FARVER[kat.gruppe] || '#60a5fa'}
 aktiv={valgt?.node_type === kat.kategori}
 onClick={() => vælgKategori({ node_type: kat.kategori, label: kat.kategori, antal: kat.antal })}
 />
 ))}
 </div> </div>
 {/* Konceptuelle noder */}
 <div style={sektStyle}> <div style={{ fontSize: 11, color: '#4a5a72', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 12 }}>
 Konceptuelle noder — {totalKon.toLocaleString('da-DK')} ekstraherede begreber
 </div> <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 }}>
 {data.konceptuelle.map(kat => (
 <KatKort
 key={kat.kategori}
 kat={{ ...kat, node_type: kat.kategori }}
 farve="#f59e0b"
 aktiv={valgt?.node_type === kat.kategori}
 onClick={() => vælgKategori({ node_type: kat.kategori, label: kat.kategori, antal: kat.antal })}
 />
 ))}
 </div> </div>
 {/* Hjerne-filer */}
 <div style={sektStyle}> <div style={{ fontSize: 11, color: '#4a5a72', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 12 }}>
 Hjerne-filer (.md) — {totalFil.toLocaleString('da-DK')} filer per mappe
 </div> <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 }}>
 {data.hjerne_filer.map(gr => (
 <div key={gr.gruppe} style={{
 padding: '11px 13px', borderRadius: 10,
 background: 'rgba(8,12,30,0.6)',
 border: '1px solid rgba(255,255,255,0.06)',
 }}> <div style={{ fontSize: 18, fontWeight: 800, color: OV_GRUPPE_FARVER[gr.gruppe] || '#94a3b8', fontFamily: 'monospace', lineHeight: 1 }}>
 {gr.antal.toLocaleString('da-DK')}
 </div> <div style={{ fontSize: 10, color: '#5a6a7a', marginTop: 4, lineHeight: 1.3 }}>
 {gr.gruppe}
 </div> </div>
 ))}
 </div> </div> </div>
 {/* Højre: dokumentliste panel */}
 {valgt && (
 <div style={{
 width: 360, flexShrink: 0,
 background: 'rgba(8,12,30,0.7)',
 border: '1px solid rgba(255,255,255,0.08)',
 borderRadius: 12,
 display: 'flex', flexDirection: 'column',
 maxHeight: 'calc(100vh - 300px)',
 position: 'sticky', top: 0,
 }}>
 {/* Header */}
 <div style={{
 padding: '12px 14px',
 borderBottom: '1px solid rgba(255,255,255,0.06)',
 display: 'flex', alignItems: 'center', justifyContent: 'space-between',
 flexShrink: 0,
 }}> <div> <div style={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0' }}>{valgt.label}</div> <div style={{ fontSize: 11, color: '#4a5a72', marginTop: 2 }}>
 {dokTotal > 0 ? `${dokTotal.toLocaleString('da-DK')} dokumenter` : '…'}
 </div> </div> <button onClick={() => { setValgt(null); setDokNoder([]); setDokTotal(0); }} style={{
 background: 'none', border: 'none', color: '#4a5a72', cursor: 'pointer', fontSize: 14, padding: 4, lineHeight: 1,
 }}></button> </div>
 {/* Liste */}
 <div style={{ overflowY: 'auto', flex: 1 }}>
 {dokLader && dokNoder.length === 0 && (
 <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 30, gap: 8, color: '#4a5a72', fontSize: 12 }}> <div style={{ width: 14, height: 14, border: '2px solid rgba(63,212,199,0.2)', borderTop: '2px solid #3fd4c7', borderRadius: '50%', animation: 'hv-spin 0.8s linear infinite' }} />
 Henter…
 </div>
 )}
 {dokNoder.map((n, i) => (
 <div key={i} style={{
 padding: '8px 14px',
 borderBottom: '1px solid rgba(255,255,255,0.03)',
 }}> <div style={{ fontSize: 11, color: '#c8d8e8', lineHeight: 1.4, fontWeight: 500 }}>
 {n.titel}
 </div>
 {n.kilde_sti && (
 <div style={{ fontSize: 10, color: '#3a4a5a', marginTop: 2, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
 title={n.kilde_sti.replace(/\\/g, '/')}>
 {n.kilde_sti.replace(/\\/g, '/').split('/').slice(-2).join('/')}
 </div>
 )}
 </div>
 ))}
 {dokNoder.length > 0 && dokNoder.length < dokTotal && (
 <button
 onClick={() => hentDok(valgt.node_type, dokOffset + 50)}
 disabled={dokLader}
 style={{
 display: 'block', width: '100%', padding: '10px 14px',
 background: 'none', border: 'none',
 color: dokLader ? '#4a5a72' : '#3fd4c7',
 fontSize: 11, cursor: dokLader ? 'default' : 'pointer',
 fontWeight: 600, textAlign: 'center',
 }}
 >
 {dokLader ? '↓ Henter…' : `↓ Vis flere (${(dokTotal - dokNoder.length).toLocaleString('da-DK')} tilbage)`}
 </button>
 )}
 </div> </div>
 )}
 </div> </div>
 );
}
// ── 3D Brain constants (module-level, computed once) ─────
const LOBE_TYPES = [
 { type: 'fakta', color: '#3fd4c7' },
 { type: 'begreb', color: '#a78bfa' },
 { type: 'entitet', color: '#fbbf24' },
 { type: 'regel', color: '#f87171' },
 { type: 'procedure', color: '#34d399' },
 { type: 'statistik', color: '#60a5fa' },
];
const NEURON_COUNT = 70;
const NEURONS = Array.from({ length: NEURON_COUNT }, (_, i) => {
 const t = i / NEURON_COUNT;
 const theta = t * Math.PI * 6.3;
 const phi = Math.acos(1 - 2 * t);
 const r = 2.2 + Math.random() * 0.9;
 const jitter = 0.4;
 return {
 id: i,
 pos: [
 r * 1.5 * Math.sin(phi) * Math.cos(theta) + (Math.random() - 0.5) * jitter,
 r * 1.0 * Math.cos(phi) + (Math.random() - 0.5) * jitter,
 r * 1.2 * Math.sin(phi) * Math.sin(theta) + (Math.random() - 0.5) * jitter,
 ],
 type: LOBE_TYPES[i % LOBE_TYPES.length],
 baseSize: 0.06 + Math.random() * 0.08,
 phaseOffset: Math.random() * Math.PI * 2,
 speed: 0.8 + Math.random() * 1.2,
 };
});
const SYNAPSES = [];
for (let i = 0; i < NEURON_COUNT; i++) {
 for (let j = i + 1; j < NEURON_COUNT; j++) {
 const dx = NEURONS[i].pos[0] - NEURONS[j].pos[0];
 const dy = NEURONS[i].pos[1] - NEURONS[j].pos[1];
 const dz = NEURONS[i].pos[2] - NEURONS[j].pos[2];
 const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
 if (dist < 1.8 && SYNAPSES.length < 160) {
 SYNAPSES.push({ from: i, to: j });
 }
 }
}
// ── 3D Brain: single neuron ───────────────────────────────
function BrainNeuron({ pos, color, phaseOffset, speed, isActive, baseSize }) {
 const meshRef = useRef();
 useFrame(({ clock }) => {
 if (!meshRef.current) return;
 const t = clock.getElapsedTime();
 const pulse = 0.5 + 0.5 * Math.sin(t * speed + phaseOffset);
 meshRef.current.material.emissiveIntensity = isActive ? 0.6 + 0.4 * pulse : 0.15 + 0.1 * pulse;
 meshRef.current.scale.setScalar(isActive ? 1 + 0.3 * pulse : 1 + 0.05 * pulse);
 });
 return (
 <mesh ref={meshRef} position={pos}> <sphereGeometry args={[baseSize, 8, 6]} /> <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.15} roughness={0.3} metalness={0.1} /> </mesh>
 );
}
// ── 3D Brain: synapse lines ───────────────────────────────
function BrainSynapses() {
 const { positions, colors } = useMemo(() => {
 const pos = new Float32Array(SYNAPSES.length * 6);
 const col = new Float32Array(SYNAPSES.length * 6);
 SYNAPSES.forEach((s, i) => {
 const from = NEURONS[s.from];
 const to = NEURONS[s.to];
 const c = new THREE.Color(from.type.color);
 pos[i*6+0]=from.pos[0]; pos[i*6+1]=from.pos[1]; pos[i*6+2]=from.pos[2];
 pos[i*6+3]=to.pos[0]; pos[i*6+4]=to.pos[1]; pos[i*6+5]=to.pos[2];
 col[i*6+0]=c.r; col[i*6+1]=c.g; col[i*6+2]=c.b;
 col[i*6+3]=c.r; col[i*6+4]=c.g; col[i*6+5]=c.b;
 });
 return { positions: pos, colors: col };
 }, []);
 return (
 <lineSegments> <bufferGeometry> <bufferAttribute attach="attributes-position" args={[positions, 3]} /> <bufferAttribute attach="attributes-color" args={[colors, 3]} /> </bufferGeometry> <lineBasicMaterial vertexColors transparent opacity={0.12} /> </lineSegments>
 );
}
// ── 3D Brain: floating dust particles ────────────────────
function BrainParticles({ count = 500 }) {
 const ref = useRef();
 const positions = useMemo(() => {
 const arr = new Float32Array(count * 3);
 for (let i = 0; i < count; i++) {
 const r = 2 + Math.random() * 3;
 const theta = Math.random() * Math.PI * 2;
 const phi = Math.acos(2 * Math.random() - 1);
 arr[i*3] = r * Math.sin(phi) * Math.cos(theta);
 arr[i*3+1] = r * Math.cos(phi) * 0.8;
 arr[i*3+2] = r * Math.sin(phi) * Math.sin(theta);
 }
 return arr;
 }, [count]);
 useFrame(({ clock }) => {
 if (ref.current) ref.current.rotation.y = clock.getElapsedTime() * 0.04;
 });
 return (
 <points ref={ref}> <bufferGeometry> <bufferAttribute attach="attributes-position" args={[positions, 3]} /> </bufferGeometry> <pointsMaterial size={0.025} color="#3fd4c7" transparent opacity={0.35} sizeAttenuation /> </points>
 );
}
// ── 3D Brain: full scene ──────────────────────────────────
function BrainScene({ aktiveTyper = [] }) {
 const groupRef = useRef();
 useFrame(({ clock }) => {
 if (groupRef.current) groupRef.current.rotation.y = clock.getElapsedTime() * 0.07;
 });
 return (
 <> <ambientLight intensity={0.15} /> <pointLight position={[8, 8, 8]} intensity={0.6} color="#3fd4c7" /> <pointLight position={[-8, -4, -8]} intensity={0.4} color="#8b5cf6" /> <Stars radius={35} depth={25} count={1200} factor={2} saturation={0.5} fade /> <BrainParticles count={500} /> <group ref={groupRef}> <BrainSynapses />
 {NEURONS.map(n => (
 <BrainNeuron key={n.id} pos={n.pos} color={n.type.color}
 phaseOffset={n.phaseOffset} speed={n.speed} baseSize={n.baseSize}
 isActive={aktiveTyper.includes(n.type.type)} />
 ))}
 </group> <OrbitControls enableZoom enablePan={false} minDistance={4} maxDistance={14} /> </>
 );
}
// ── Live stat-kort med count-up ───────────────────────────
function LiveStatKort({ label, target, farve, ikon, delay = 0 }) {
 const value = useCountUp(target ?? 0);
 return (
 <div style={{
 background: `linear-gradient(135deg, ${farve}12, rgba(2,5,16,0.9))`,
 border: `1px solid ${farve}35`,
 borderRadius: 10,
 padding: '12px 14px',
 display: 'flex', flexDirection: 'column', gap: 2,
 animation: `hv-fadein 0.4s ease ${delay}s both`,
 position: 'relative', overflow: 'hidden',
 }}> <div style={{ position: 'absolute', top: -4, right: -4, fontSize: 36, opacity: 0.07, lineHeight: 1, userSelect: 'none' }}>{ikon}</div> <div style={{ fontSize: 9, color: farve + 'aa', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2 }}>
 {label}
 </div> <div style={{ fontSize: 26, fontWeight: 800, color: farve, lineHeight: 1.1, fontFamily: '"JetBrains Mono","Fira Code",monospace' }}>
 {target == null ? '—' : value.toLocaleString('da-DK')}
 </div> <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${farve}60, transparent)` }} /> </div>
 );
}
// ── Hjerne Hero — 3D brain + live stats + agenter ────────
function HjerneHero({ stats, agenter }) {
 const aktiveTyper = agenter
 .filter(a => a.status === 'aktiv' || a.status === 'koerer')
 .flatMap(a => {
 const typeMap = {
 'viden-udtrak': ['fakta', 'begreb', 'entitet', 'regel', 'procedure', 'statistik'],
 'analyse': ['fakta', 'statistik'],
 'kode': ['procedure', 'regel'],
 'overvaagning': ['fakta', 'entitet'],
 };
 return typeMap[a.agent_id] || [];
 });
 const s = stats || {};
 const leftStats = [
 { label: 'Firmaviden noder', target: s.firmaviden, farve: '#3fd4c7', ikon: '' },
 { label: 'Fakta', target: s.noder_fakta, farve: '#22d3ee', ikon: '' },
 { label: 'Begreb', target: s.noder_begreb, farve: '#a78bfa', ikon: '' },
 { label: 'Entitet', target: s.noder_entitet, farve: '#fbbf24', ikon: '' },
 { label: 'Regel', target: s.noder_regel, farve: '#f87171', ikon: '' },
 { label: 'Procedure', target: s.noder_procedure, farve: '#34d399', ikon: '' },
 { label: 'Statistik', target: s.noder_statistik, farve: '#60a5fa', ikon: '' },
 ];
 const aktiveCount = agenter.filter(a => a.status === 'aktiv' || a.status === 'koerer').length;
 return (
 <div style={{
 display: 'flex', gap: 16,
 height: 'calc(100vh - 260px)', minHeight: 480,
 animation: 'hv-fadein 0.4s ease',
 }}>
 {/* Left: live node type stats */}
 <div style={{
 width: 196, flexShrink: 0,
 display: 'flex', flexDirection: 'column', gap: 7,
 overflowY: 'auto',
 }}> <div style={{ fontSize: 9, color: '#2a3a50', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 2 }}>
 Live noder
 </div>
 {leftStats.map((st, i) => (
 <LiveStatKort key={st.label} {...st} delay={i * 0.05} />
 ))}
 </div>
 {/* Center: 3D brain canvas */}
 <div style={{
 flex: 1, minWidth: 0, position: 'relative',
 borderRadius: 16, overflow: 'hidden',
 background: 'radial-gradient(ellipse at center, #050d1e 0%, #020510 100%)',
 border: '1px solid rgba(63,212,199,0.1)',
 boxShadow: '0 0 60px rgba(63,212,199,0.05), inset 0 0 80px rgba(8,15,40,0.8)',
 }}> <Canvas camera={{ position: [0, 0, 9], fov: 50 }} style={{ width: '100%', height: '100%' }}> <BrainScene aktiveTyper={aktiveTyper} /> </Canvas> <div style={{
 position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)',
 fontSize: 9, color: 'rgba(63,212,199,0.35)', fontFamily: 'monospace',
 letterSpacing: 1.5, textTransform: 'uppercase', whiteSpace: 'nowrap',
 pointerEvents: 'none',
 }}>
 Drag · rotate &nbsp;|&nbsp; scroll · zoom
 </div> <div style={{ position: 'absolute', top: 14, left: 14, display: 'flex', alignItems: 'center', gap: 6, pointerEvents: 'none' }}> <div style={{
 width: 6, height: 6, borderRadius: '50%', background: '#3fd4c7',
 boxShadow: '0 0 10px #3fd4c7', animation: 'hv-live-dot 1.2s ease infinite',
 }} /> <span style={{ fontSize: 10, color: '#3fd4c7', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase' }}>Live</span> </div> </div>
 {/* Right: agent status panel */}
 <div style={{
 width: 216, flexShrink: 0,
 background: 'rgba(4,8,20,0.7)', border: '1px solid rgba(255,255,255,0.07)',
 borderRadius: 16, display: 'flex', flexDirection: 'column', overflow: 'hidden',
 }}> <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}> <div style={{ fontSize: 9, color: '#2a3a50', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2 }}>
 OS Agenter
 </div> </div> <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
 {agenter.length === 0 && (
 <div style={{ padding: '20px 14px', color: '#2a3a50', fontSize: 11, textAlign: 'center' }}> <div style={{ width: 14, height: 14, border: '2px solid rgba(63,212,199,0.15)', borderTop: '2px solid #3fd4c7', borderRadius: '50%', animation: 'hv-spin 0.8s linear infinite', margin: '0 auto 8px' }} />
 Henter…
 </div>
 )}
 {agenter.map(a => {
 const isAktiv = a.status === 'aktiv' || a.status === 'koerer';
 const farve = isAktiv ? '#22c55e'
 : a.status === 'advarsel' ? '#f59e0b'
 : a.status === 'fejl' ? '#ef4444'
 : '#2a3a50';
 return (
 <div key={a.agent_id} style={{
 padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 9,
 borderBottom: '1px solid rgba(255,255,255,0.03)',
 }}> <div style={{
 width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
 background: farve,
 boxShadow: isAktiv ? `0 0 8px ${farve}` : 'none',
 animation: isAktiv ? 'hv-live-dot 1.2s ease infinite' : 'none',
 }} /> <div style={{ flex: 1, minWidth: 0 }}> <div style={{
 fontSize: 10, fontWeight: isAktiv ? 700 : 400,
 color: isAktiv ? farve : '#5a6a7a',
 fontFamily: 'monospace',
 overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
 }}>
 {a.agent_id}
 </div>
 {isAktiv && a.aktuel_opgave && (
 <div style={{ fontSize: 9, color: '#3a4a5a', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
 {a.aktuel_opgave}
 </div>
 )}
 </div> </div>
 );
 })}
 </div> <div style={{ padding: '10px 14px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: 10 }}> <div style={{ flex: 1, textAlign: 'center' }}> <div style={{ fontSize: 20, fontWeight: 800, color: aktiveCount > 0 ? '#22c55e' : '#2a3a50', fontFamily: 'monospace' }}>
 {aktiveCount}
 </div> <div style={{ fontSize: 9, color: aktiveCount > 0 ? '#22c55e99' : '#2a3a50', textTransform: 'uppercase', letterSpacing: 0.7 }}>Aktive</div> </div> <div style={{ flex: 1, textAlign: 'center' }}> <div style={{ fontSize: 20, fontWeight: 800, color: '#4a5a72', fontFamily: 'monospace' }}>
 {agenter.length}
 </div> <div style={{ fontSize: 9, color: '#2a3a50', textTransform: 'uppercase', letterSpacing: 0.7 }}>Total</div> </div> </div> </div> </div>
 );
}
// ══════════════════════════════════════════════════════════
// HOVED-KOMPONENT
// ══════════════════════════════════════════════════════════
export default function HjerneVaekst() {
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(true);
 const [fejl, setFejl] = useState(null);
 const [visning, setVisning] = useState('hjerne');
 const [aktiveAgenter, setAktive] = useState([]);
 const [alleAgenter, setAlleAgenter] = useState([]);
 const [aktiveNodeIds, setAktiveNodeIds] = useState([]);
 const [selectedNode, setSelectedNode] = useState(null);
 const [focusNodeId, setFocusNodeId] = useState(null);
 const hent = useCallback(async () => {
 try {
 const r = await apiFetch('/os/hjerne/vaekst');
 setData(r);
 setFejl(null);
 } catch (e) {
 setFejl(e.message);
 } finally {
 setLoading(false);
 }
 }, []);
 useEffect(() => {
 hent();
 const id = setInterval(hent, 30000);
 return () => clearInterval(id);
 }, [hent]);
 // Poll agent-status hvert 8. sekund for live aktivitet
 useEffect(() => {
 const hentAgenter = async () => {
 try {
 const r = await apiFetch('/os/hjerne/agenter');
 const alle = r?.agenter || [];
 setAlleAgenter(alle);
 setAktive(alle
 .filter(a => a.status === 'aktiv' || a.status === 'koerer')
 .map(a => a.agent_id));
 } catch (_) {}
 };
 hentAgenter();
 const id = setInterval(hentAgenter, 8000);
 return () => clearInterval(id);
 }, []);
 // Poll viden-aktivitet hvert 10. sekund — hvilke noder henter agenter aktivt?
 useEffect(() => {
 const hentVidenAktivitet = async () => {
 try {
 const r = await apiFetch('/os/hjerne/viden-aktivitet?minutter=5');
 if (!r?.migration_mangler) {
 setAktiveNodeIds(r?.aktive_node_ids || []);
 }
 } catch (_) {}
 };
 hentVidenAktivitet();
 const id = setInterval(hentVidenAktivitet, 10000);
 return () => clearInterval(id);
 }, []);
 const s = data?.stats || {};
 const stats_kort = [
 { label: 'Firmaviden', value: s.firmaviden, farve: '#3fd4c7', ikon: '', delay: 0 },
 { label: 'Total læring',value: s.total_laering, farve: '#8b5cf6', ikon: '', delay: 0.08 },
 { label: 'Beslutninger',value: s.beslutninger, farve: '#f59e0b', ikon: '', delay: 0.16 },
 { label: 'Total filer', value: s.total_filer, farve: '#22c55e', ikon: '', delay: 0.24 },
 { label: 'Alle noder', value: (s.firmaviden || 0) + (s.total_laering || 0), farve: '#16a34a', ikon: '', delay: 0.32 },
 ];
 const tabs = [
 { id: 'hjerne', label: 'Hjerne', ikon: '' },
 { id: 'oversigt', label: 'Indholdsoversigt', ikon: '' },
 { id: 'graf', label: 'Videns-graf', ikon: '' },
 { id: 'tidslinje', label: 'Vækst-tidslinje', ikon: '' },
 { id: 'filer', label: 'Seneste filer', ikon: '' },
 { id: 'mapper', label: 'Mapper', ikon: '' },
 ];
 return (
 <> <style>{CSS}</style> <div style={{
 minHeight: 'calc(100vh - 52px)',
 background: '#03060f',
 color: '#e2e8f0',
 fontFamily: '"Inter",-apple-system,sans-serif',
 padding: '24px 28px',
 overflowY: 'auto',
 }}>
 {/* ── Header ─────────────────────────────────────────── */}
 <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}> <div> <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#e2e8f0', letterSpacing: -0.5 }}>
 Hjerne-vækst
 </h1> <div style={{ marginTop: 4, fontSize: 12, color: '#4a5a72', fontFamily: 'monospace' }}>
 {data?.indsamlet_kl
 ? `Sidst synkroniseret: ${fmt_dato(data.indsamlet_kl)}`
 : 'Afventer første sync — kør hjerne-stats-sync.ps1'}
 </div> </div> <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
 {loading && (
 <div style={{ width: 16, height: 16, border: '2px solid rgba(63,212,199,0.2)', borderTop: '2px solid #3fd4c7', borderRadius: '50%', animation: 'hv-spin 0.8s linear infinite' }} />
 )}
 <button onClick={hent} style={{
 padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
 background: 'rgba(63,212,199,0.08)', border: '1px solid rgba(63,212,199,0.25)',
 color: '#3fd4c7', fontSize: 11, fontWeight: 700,
 }}>↺ Opdater</button> </div> </div>
 {/* ── Fejl ───────────────────────────────────────────── */}
 {fejl && (
 <div style={{ padding: '14px 18px', borderRadius: 10, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#fca5a5', fontSize: 12, marginBottom: 20 }}>
 {fejl === 'HTTP 500' ? 'Hjerne-stats tabel mangler — kør migration 041 og hjerne-stats-sync.ps1' : fejl}
 </div>
 )}
 {/* ── Stats-kort ─────────────────────────────────────── */}
 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 28 }}>
 {stats_kort.map(k => <StatKort key={k.label} {...k} />)}
 </div>
 {/* ── Tabs ───────────────────────────────────────────── */}
 <div style={{ display: 'flex', gap: 6, marginBottom: 20, borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: 0 }}>
 {tabs.map(t => (
 <button
 key={t.id}
 onClick={() => setVisning(t.id)}
 style={{
 padding: '9px 16px', border: 'none', cursor: 'pointer',
 background: 'none',
 color: visning === t.id ? '#3fd4c7' : '#4a5a72',
 fontWeight: visning === t.id ? 700 : 500,
 fontSize: 12,
 borderBottom: visning === t.id ? '2px solid #3fd4c7' : '2px solid transparent',
 transition: 'all 0.2s',
 display: 'flex', alignItems: 'center', gap: 6,
 }}
 > <span>{t.ikon}</span> {t.label}
 </button>
 ))}
 </div>
 {/* ── Hjerne (3D brain) ──────────────────────────────── */}
 {visning === 'hjerne' && (
 <HjerneHero stats={data?.stats} agenter={alleAgenter} />
 )}
 {/* ── Videns-graf ────────────────────────────────────── */}
 {visning === 'graf' && (
 <div style={{ height: 'calc(100vh - 320px)', minHeight: 420, animation: 'hv-fadein 0.3s ease', display: 'flex', gap: 10 }}>
 {/* Venstre: søgepanel — alle noder */}
 <GrafSoegPanel
 selectedNodeId={selectedNode?.id}
 onNodeSelect={node => {
 setSelectedNode(prev => prev?.id === node.id ? null : node);
 setFocusNodeId(null);
 }}
 />
 {/* Midt: Canvas-graf — alle DB-noder */}
 <div style={{ flex: 1, minWidth: 0, height: '100%', position: 'relative' }}> <CanvasGraf
 selectedNodeId={selectedNode?.id}
 aktiveNodeIds={aktiveNodeIds}
 onNodeClick={node => {
 if (!node) { setSelectedNode(null); setFocusNodeId(null); return; }
 setSelectedNode(prev => prev?.id === node.id ? null : node);
 setFocusNodeId(null);
 }}
 focusNodeId={focusNodeId}
 /> </div>
 {/* Højre: detalje-panel */}
 {selectedNode && (
 selectedNode.gruppe === 'klientsager' ||
 /^sag-\d+$/.test(selectedNode.id) ||
 selectedNode.id === 'klient-sager-hub'
 ) && (
 <SagPanel
 node={selectedNode}
 onClose={() => { setSelectedNode(null); setFocusNodeId(null); }}
 />
 )}
 {selectedNode && selectedNode.gruppe !== 'klientsager' &&
 !/^sag-\d+$/.test(selectedNode.id) &&
 selectedNode.id !== 'klient-sager-hub' && (
 <FilPanel
 node={selectedNode}
 graph={data?.graph || { nodes: [], edges: [] }}
 onFocusNode={id => {
 setFocusNodeId(id);
 const raw = (data?.graph?.nodes || []).find(n => n.id === id);
 if (raw) setSelectedNode({ ...raw, gruppe: deriveGroupe(raw) });
 }}
 onClose={() => { setSelectedNode(null); setFocusNodeId(null); }}
 />
 )}
 </div>
 )}
 {/* ── Vækst-tidslinje ────────────────────────────────── */}
 {visning === 'tidslinje' && (
 <div style={{ animation: 'hv-fadein 0.3s ease' }}> <div style={{
 background: 'rgba(8,12,30,0.6)', border: '1px solid rgba(255,255,255,0.06)',
 borderRadius: 12, padding: '20px 20px 12px',
 }}> <div style={{ fontSize: 11, color: '#4a5a72', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14, fontWeight: 700 }}>
 Nye filer per dag — seneste 30 dage
 </div>
 {data?.tidslinje?.length > 0
 ? <VaekstTidslinje tidslinje={data.tidslinje} />
 : <div style={{ color: '#4a5a72', fontSize: 12, padding: '30px 0', textAlign: 'center' }}>Ingen tidslinje-data endnu</div>
 }
 </div>
 {data?.tidslinje?.length > 0 && (
 <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
 {[
 { label: 'Filer i dag', value: data.tidslinje.find(d => d.dato === new Date().toISOString().slice(0,10))?.antal || 0, farve: '#3fd4c7' },
 { label: 'Denne uge', value: data.tidslinje.filter(d => new Date(d.dato) >= new Date(Date.now() - 7*86400000)).reduce((s, d) => s + d.antal, 0), farve: '#8b5cf6' },
 { label: 'Seneste 30 dage', value: data.tidslinje.reduce((s, d) => s + d.antal, 0), farve: '#22c55e' },
 ].map(item => (
 <div key={item.label} style={{
 padding: '10px 16px', borderRadius: 10,
 background: `${item.farve}10`, border: `1px solid ${item.farve}25`,
 display: 'flex', flexDirection: 'column', gap: 2,
 }}> <div style={{ fontSize: 10, color: item.farve + '99', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>{item.label}</div> <div style={{ fontSize: 24, fontWeight: 800, color: item.farve, fontFamily: 'monospace' }}>{item.value}</div> </div>
 ))}
 </div>
 )}
 </div>
 )}
 {/* ── Seneste filer ───────────────────────────────────── */}
 {visning === 'filer' && (
 <div style={{ animation: 'hv-fadein 0.3s ease' }}> <SenesteFiler filer={data?.seneste_filer} /> </div>
 )}
 {/* ── Indholdsoversigt ───────────────────────────────── */}
 {visning === 'oversigt' && <IndholdsoversigtsVisning />}
 {/* ── Mapper ─────────────────────────────────────────── */}
 {visning === 'mapper' && <MappeBrowser />}
 </div> </>
 );
}